// Munich, "Christmas market in the snow": a December night at the Christkindlmarkt on Marienplatz, gentle snow falling.
// Back to front: snow-cloud sky lit gold from below -> hazy old-town roofs -> the Frauenkirche's twin brick towers with
// their green domes, a nave's width apart with the west gable between them, rising behind the Rathaus roofs to the
// north-west of its tower (the belfries glow on each strike) -> far snow -> the Neues Rathaus, its asymmetrical front
// in two parts as seen from the square: the western tract (1899-1908) with the 85 m tower about 30 % along from the
// Weinstraße corner and its Glockenspiel oriel, whose evening figures (the night watchman, the angel and the Münchner
// Kindl) come out and turn once per loop while the coopers dance above; then, past a turret marking the join, the
// longer eastern first phase (1867-74) with its five-axis central risalit under a big gable crowned by the knight with
// the weather vane, and the lit loggia on the 2nd floor; snow-covered roof, spiky neo-Gothic gables and pinnacles
// -> old gabled houses on the west side, a paper star in a window -> the Mariensäule with its golden Virgin -> a huge
// Christmas tree with twinkling lights -> two rows of red-roofed stalls under snow with light strings, a chestnut
// roaster, Glühwein steam, people in hats strolling between them, a father pulling his daughter on a sledge -> mid
// snow -> our own stall: its post, the carved valance with swinging gingerbread hearts, a lantern and a steaming mug on
// the snowy counter -> near snow -> vignette.
// A lit pretzel-bakery sign stands on a timber scaffold behind the Glühwein stall, next door to the bakery (its face is
// the exported `billboard`; render(t, { pitch }) swaps its ad, render(t, { billboard: false }) leaves the sign and its
// scaffold out).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, text, textWidth, makePitch, gradeAd, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const RIDGE = 104;                  // Neues Rathaus roof ridge
const EAVE = 124;                   // top of its facade (the openwork parapet)
const RX0 = 94, RX1 = 400;          // facade span, corner turrets included (the 98.5 m front on Marienplatz)
// We face north, so west is on the left. The tower stands in the western tract, ~29 m along the front from the
// Weinstraße corner (OpenStreetMap footprint): ~30 % of the facade, well left of its centre.
const TX = 185;                     // tower centre column (square shaft TX-11 .. TX+11)
const T_GAL = 56;                   // gallery on top of the square shaft
const ORIEL = { x0: TX - 7, x1: TX + 7, up: 93, lo: 107 };   // Glockenspiel oriel: first row of its upper and lower arcades
const CLOCK = { x: TX, y: 64 };
const JOIN = 251;                   // where the western tract meets the older eastern part (48 m of the front): a turret
const RIS = 324, RIS_HW = 19;       // the eastern part's five-axis central risalit (centre, half-width), ~75 % along
const FK = [135, 159];              // Frauenkirche tower centre columns (13 px wide), north-west of the Rathaus tower
const MS = { x: 292, top: 95 };     // Mariensäule: column centre, top of the golden Virgin's crown
const TREE = { x: 360, top: 74, base: 194, hw: 33 };
const GROUND = 186;                 // the square behind the stalls
const FGND = 228;                   // foot of the front row of stalls
const AISLE = 247;                  // feet of the people strolling in front of them
const POST = 406;                   // our stall's corner post (left edge, 7 px wide)
const COUNTER = 234;                // top of our counter
const MUG = { x: 376, y: 221 };     // Glühwein mug on the counter (top-left)
const LANTERN = { x: 457, y: 30 };  // hanging lantern (top of its ring)
const ROAST = { x: 62, y: 206 };    // chestnut roaster cart (top-left of the stove)
const BB = { x: 204, y: 126, w: 46, h: 20 };   // the pretzel sign's face on its scaffold (the pitch is painted exactly here)

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  return { c, x, put, alpha, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line
function line(c, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
// the same rows straight into a painter at (x0, y0)
function stamp(p, rows, key, x0, y0) {
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(x0 + i, y0 + y, col); } });
}
// dithered soft blob for steam and smoke
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
const snowPx = (h1, h2) => h1 < 0.7 ? '#eef2fc' : h2 < 0.5 ? '#c8d2ea' : '#a6b2d4';
const tintCache = {};
function tint(col, tcol, a) {
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * a).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
// signs: FONT3 via text(), plus the umlaut the market words need and their own K and W: FONT3's read as H on a sign
// (KERZEN as HERZEN, GLÜHWEIN as GLÜHHEIN), so K gets a clear diagonal and W five columns
const SIGN_GLYPHS = {
  'Ü': ['101', '000', '101', '101', '111'],
  K: ['101', '110', '100', '110', '101'],
  W: ['10001', '10001', '10101', '10101', '01010'],
};
const signWidth = s => [...s].reduce((w, ch) => w + (SIGN_GLYPHS[ch]?.[0].length ?? 3) + 1, -1);
function sign(c, s, x, y, col) {
  let cx = x;
  for (const ch of s) {
    const g = SIGN_GLYPHS[ch];
    if (g) glyph(c, g, cx, y, col); else text(c, ch, cx, y, col);
    cx += (g ? g[0].length : 3) + 1;
  }
  return cx - x - 1;
}

/* ================= Sky ================= */
const SKY = ['#04060f', '#060918', '#080c21', '#0b102a', '#0f1533', '#141a3c', '#1a1f45', '#21244d', '#2a2953',
  '#352e58', '#42335b', '#51395c', '#62405b', '#744858', '#865254'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 172), 1.4);
    v += Math.max(0, 1 - Math.hypot((i - 280) / 300, (y - 178) / 100)) * 0.3;   // the market's gold on the snow clouds
    return v;
  });
}
const STARS = [];
{ const r = rng(41); for (let i = 0; i < 22; i++) STARS.push({ x: Math.floor(r() * W), y: 2 + Math.floor(r() * 56), k: 20 + Math.floor(r() * 50), ph: r(), b: 0.25 + r() * 0.45 }); }

/* ================= Snow clouds (tileable; dim tops, bellies lit gold by the market) ================= */
const CLOUD_DEFS = [
  { seed: 5, y: 0, h: 58, banks: 3, k: 1, sx: 1.3, pal: { top: '#282e50', body: '#141932', core: '#11152c', bot: '#383052', bot2: '#252644' } },
  { seed: 23, y: 34, h: 60, banks: 4, k: 2, sx: 1.25, pal: { top: '#343a5e', body: '#1b1f3e', core: '#171a36', bot: '#6c4c5a', bot2: '#473a54' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 70 + r() * 110, base = h * (0.45 + r() * 0.35);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = (7 + r() * 13) * def.sx, ry = 3.5 + r() * 6;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.4;             // flat bellies
      if (v > best) best = v;
    }
    best += 0.14 * Math.sin(TAU * 9 * x / W + y * 0.37 + def.seed) * Math.sin(TAU * 4 * x / W - y * 0.21);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Hazy old-town roofs behind the Rathaus ================= */
function renderFar() {
  const p = painter(W, H), r = rng(88);
  const BODY = '#1c1a37';
  let x = -6;
  while (x < W + 6) {
    const w = 10 + Math.floor(r() * 14), ridge = 88 + Math.floor(r() * 16), eave = ridge + 6 + Math.floor(r() * 5);
    const gable = r() < 0.45;
    for (let i = x; i < x + w; i++) {
      const top = gable ? Math.round(ridge + Math.abs(i - (x + w / 2)) / (w / 2) * (eave - ridge)) : ridge + (i === x || i === x + w - 1 ? 1 : 0);
      for (let y = top; y < 200; y++) {
        let col = BODY;
        if (y === top) col = '#62678f';
        else if (y === top + 1) col = '#474b73';
        else if (!gable && y < eave) col = bayer(i, y) < 0.35 ? '#3c3f66' : '#32345a';   // snowy roof band
        p.put(i, y, col);
      }
    }
    if (r() < 0.4) {                                          // chimney with a snow cap
      const cx = x + 2 + Math.floor(r() * (w - 4));
      for (let y = ridge - 4; y < ridge + 2; y++) { p.put(cx, y, BODY); p.put(cx + 1, y, BODY); }
      p.put(cx, ridge - 5, '#62678f'); p.put(cx + 1, ridge - 5, '#62678f');
    }
    for (let y = eave + 3; y < 190; y += 5) for (let i = x + 2; i < x + w - 2; i += 3) if (r() < 0.18) p.put(i, y, r() < 0.7 ? '#86644a' : '#4c5480');
    x += w;
  }
  return p.done();
}

/* ================= Frauenkirche: twin brick towers with green domes ================= */
const FK_HW = 6;                                     // square shafts, 13 px
const FK_BELFRY = [61, 75];                          // octagonal belfry stage (rows), 11 px
const FK_GABLE = 84;                                 // apex of the west gable between the towers
// [row, half-width] of each "Welsche Haube" from its foot up: a tall copper bulb no wider than the tower, taller than it
// is wide, narrowing into the neck of its lantern
const FK_DOME = [[59, 5], [58, 6], [57, 6], [56, 6], [55, 6], [54, 6], [53, 6], [52, 6], [51, 6], [50, 5], [49, 5], [48, 5],
  [47, 4], [46, 4], [45, 3], [44, 2], [43, 1]];
const FK_LANTERN = 42;                               // top of the dome's neck: the lantern rises from here
const COPPER = ['#1d463c', '#2f6c58', '#3f8a6a', '#52a07e', '#6cbc94', '#8ad0aa'];
// the lantern on the dome's neck, top down: the spike, the gilt ball, a small cupola, a cornice, a lit window, its base
const FK_TOP = ['.y.', '.g.', '.g.', '.Bb', '.m.', 'LmD', 'lMd', 'lWd', 'lwd', 'lMd'];
const FK_TOP_KEY = { y: '#fff0a0', g: '#c8a050', B: '#fff0a0', b: '#a07a38', m: COPPER[3], L: COPPER[5], D: COPPER[0],
  l: COPPER[4], M: COPPER[2], d: COPPER[1], W: '#fff0b0', w: '#f0b860' };
const BRICK = ['#3a1e26', '#542a2c', '#703832', '#8e4c3a', '#ae6448', '#cc8058'];
function renderFK() {
  const p = painter(W, H), put = p.put;
  // the west front between the towers, a nave's width apart: a pointed brick gable, snow on its copings, a lit rose
  // window; open sky above it between the belfries and the domes
  const fc = (FK[0] + FK[1]) >> 1;
  for (let i = FK[0] + FK_HW + 1; i < FK[1] - FK_HW; i++) {
    const top = FK_GABLE + Math.round(Math.abs(i - fc) * 1.2);
    for (let y = top; y <= RIDGE + 2; y++) {
      const v = 0.3 + 0.3 * (y - FK_GABLE) / 20 + (i < fc ? 0.08 : i > fc ? -0.06 : 0) + (bayer(i, y) - 0.5) * 0.2;
      put(i, y, y === top ? (i <= fc ? '#dfe6f4' : '#a6b2d4') : y === top + 1 ? (i < fc ? '#d09468' : '#54322e') : BRICK[clamp(Math.floor(v * 5), 0, 5)]);
    }
  }
  put(fc, FK_GABLE - 1, '#d8b060');                                          // a little finial on the apex
  for (const [dx, dy, col] of [[0, -1, '#e8a050'], [-1, 0, '#e8a050'], [0, 0, '#fff0b0'], [1, 0, '#c07838'], [0, 1, '#c07838']])
    put(fc + dx, FK_GABLE + 8 + dy, col);                                    // the rose window
  for (const cx of FK) {
    // square shaft below, octagonal belfry above; floodlit from below, lit on the left face
    for (let y = FK_BELFRY[0]; y <= RIDGE + 2; y++) {
      const oct = y < FK_BELFRY[1], hw = oct ? FK_HW - 1 : FK_HW;
      for (let dx = -hw; dx <= hw; dx++) {
        let v = 0.36 + 0.36 * clamp((y - FK_BELFRY[0]) / 44, 0, 1);
        if (dx <= -hw + 1) v += 0.26; else if (dx >= hw - 1) v -= 0.24;
        if (oct && Math.abs(dx) === hw - 2) v -= 0.1;              // facets of the octagon
        if (!oct && Math.abs(dx) === 3) v -= 0.07;                 // shallow pilaster strips
        if (y % 4 === 0) v -= 0.05;                                // brick courses
        put(cx + dx, y, BRICK[clamp(Math.floor(v * 5 + bayer(cx + dx, y) - 0.4), 0, 5)]);
      }
    }
    // cornices: under the dome, and between the belfry and the shaft (snow on both)
    for (let dx = -FK_HW; dx <= FK_HW; dx++) {
      put(cx + dx, 60, dx < -2 ? '#d8a276' : dx < 2 ? '#a86e4e' : '#5e3632');
      put(cx + dx, FK_BELFRY[1], dx < -2 ? '#d09468' : dx < 2 ? '#9a5c40' : '#54322e');
      put(cx + dx, FK_BELFRY[1] + 1, '#2e1820');
      if (hash(cx + dx, 1, 31) < 0.65) put(cx + dx, FK_BELFRY[1] - 1, snowPx(hash(cx + dx, 2, 31), 0.3));
    }
    // belfry lancets (their glow is drawn per frame) and slit windows in the shaft
    for (const ox of [-2, 2]) for (let y = 64; y <= 71; y++) put(cx + ox, y, y === 64 ? '#2e1820' : '#160a0e');
    for (const wy of [81, 93]) for (let y = wy; y < wy + 6; y++) put(cx, y, y === wy ? '#2a1418' : '#170b10');
    // the dome: copper green, round, lit from below-left, two ribs converging on the lantern, a glint on the bulge
    for (const [y, hw] of FK_DOME) for (let dx = -hw; dx <= hw; dx++) {
      const u = (dx + 0.5) / (hw + 0.5), lift = (59 - y) / 16;          // u: -1 lit edge .. 1 shadowed edge
      let v = 0.62 - 0.55 * u - 0.12 * u * u + 0.12 * lift;
      if (y >= 58) v -= 0.14;                                            // the foot, in the cornice's shadow
      if (hw >= 3 && Math.abs(Math.abs(dx) - Math.round(hw * 0.5)) < 0.5) v -= 0.16;   // ribs
      let col = COPPER[clamp(Math.floor(v * 5 + bayer(cx + dx, y) - 0.3), 0, 5)];
      if (dx === -hw + 1 && y >= 54 && y <= 56) col = '#cdf6de';
      put(cx + dx, y, col);
    }
    stamp(p, FK_TOP, FK_TOP_KEY, cx - 1, FK_LANTERN - FK_TOP.length + 1);
    for (const dx of [-6, 6]) if (hash(cx + dx, 3, 31) < 0.8) put(cx + dx, 59, '#eef2fc');
  }
  return p.done();
}

/* ================= Old gabled houses on the west side, a pale house on the east side ================= */
const WEST = [
  { x0: -2, x1: 31, top: 92, kind: 'step', pal: ['#3a2e36', '#5a4438', '#80604a', '#a8845c', '#caa472'] },
  { x0: 31, x1: 63, top: 84, kind: 'curve', pal: ['#382834', '#5a3a40', '#805656', '#a47672', '#c69a8a'] },
  { x0: 63, x1: 94, top: 98, kind: 'roof', pal: ['#2a2e34', '#424c46', '#5e6c5e', '#7e8e76', '#a0ae90'] },
];
const EAST = { x0: 400, x1: 482, top: 94, pal: ['#2a2630', '#403a40', '#5a5250', '#766a60', '#928474'] };
const HWIN = [];                                     // house windows that switch on and off: { id, x, y, w, h, n, off }
let STAR_WIN = null;                                 // the window with the paper Advent star
function houseOutline(hs, y) {                       // half-width of a west house's gable at row y (-1: above it)
  const c = (hs.x0 + hs.x1) / 2, full = (hs.x1 - hs.x0) / 2, g = y - hs.top;
  if (g < 0) return -1;
  if (hs.kind === 'roof' || g >= 22) return full;
  if (hs.kind === 'step') return Math.max(2, full * Math.ceil((g + 1) / 5.5) / 4);
  return g < 4 ? 2.5 : 3 + (full - 3) * smooth(3, 21, g);                // baroque bell gable: a crown, then an S-curve flare
}
function drawHouse(p, hs, r) {
  const c = (hs.x0 + hs.x1) / 2, P = hs.pal, face0 = hs.kind === 'roof' ? hs.top + 12 : hs.top;
  for (let y = hs.top; y < GROUND + 2; y++) {
    const hw = houseOutline(hs, y), up = houseOutline(hs, y - 1);
    for (let i = Math.ceil(c - hw); i <= Math.floor(c + hw); i++) {
      let col;
      if (hs.kind === 'roof' && y < face0) {                              // snow-covered roof band with a dormer
        const u = (y - hs.top) / 12;
        col = y === hs.top ? '#e6ecf8' : u < 0.6 ? (bayer(i, y) < 0.6 - u * 0.5 ? '#c4cae4' : '#9ea6c8') : '#7a82a6';
        if (y === face0 - 1) col = '#3a3448';
      } else {
        let v = 0.34 + 0.4 * (y - face0) / (GROUND - face0);
        if (i <= c - hw + 1) v += 0.16; else if (i >= c + hw - 1) v -= 0.18;
        v += (hash(i, y, 5) - 0.5) * 0.06;
        col = P[clamp(Math.floor(v * 4 + bayer(i, y) - 0.3), 0, 4)];
        if (hs.kind === 'step' && Math.abs(i - c) > up) col = '#e6ecf8';        // snow on each step of the gable
        else if (Math.abs(i - c) > up + 0.5 && hs.kind === 'curve') col = hash(i, y, 6) < 0.7 ? '#dfe6f4' : P[3];
        else if (y === hs.top + 22 && hs.kind !== 'roof') col = P[1];     // cornice under the gable
      }
      p.put(i, y, col);
    }
  }
  if (hs.kind === 'curve') { p.put(c, hs.top - 1, P[3]); p.put(c, hs.top - 2, '#e6ecf8'); }   // finial ball
  if (hs.kind === 'step') for (let y = hs.top + 8; y < hs.top + 13; y++) p.put(Math.round(c), y, y === hs.top + 8 ? P[1] : '#ffd894');   // lit attic slit
  if (hs.kind === 'roof') {                                             // dormer and chimney
    const dx = Math.round(c) - 2;
    for (let y = hs.top + 3; y < hs.top + 10; y++) for (let i = 0; i < 5; i++) {
      const edge = y === hs.top + 3 || i === 0 || i === 4;
      p.put(dx + i, y, y === hs.top + 3 ? '#eef2fc' : edge ? P[1] : y < hs.top + 6 ? '#ffd690' : '#eea85a');
    }
    for (let y = hs.top - 6; y < hs.top; y++) { p.put(hs.x0 + 5, y, P[1]); p.put(hs.x0 + 6, y, P[0]); }
    p.put(hs.x0 + 5, hs.top - 7, '#eef2fc'); p.put(hs.x0 + 6, hs.top - 7, '#c8d2ea');
  }
  // windows: three a floor, snow on the sills
  for (let y = face0 + (hs.kind === 'roof' ? 5 : 27); y < GROUND - 8; y += 12) for (const ox of [-9, -1, 7]) {
    const x = Math.round(c + ox) - 1, v = r();
    const lit = v < 0.42, toggle = !lit && v < 0.6;
    for (let j = 0; j < 6; j++) for (let i = 0; i < 3; i++)
      p.put(x + i, y + j, lit ? (j < 2 ? '#ffd690' : '#f0aa5c') : (j < 2 && i === 0 ? '#2c2a44' : '#15131f'));
    if (lit && hash(x, y, 7) < 0.5) for (let j = 0; j < 6; j++) p.put(x + 1, y + j, '#9a5a34');   // mullion
    for (let i = -1; i <= 3; i++) { p.put(x + i, y + 6, hash(x + i, y, 8) < 0.8 ? '#eef2fc' : '#c8d2ea'); p.put(x + i, y + 7, P[1]); }
    if (toggle) HWIN.push({ id: HWIN.length, x, y, w: 3, h: 6, n: 3 + Math.floor(r() * 5), off: r() });
    if (hs.kind === 'curve' && ox === -1 && y === face0 + 39) STAR_WIN = { x: x + 1, y: y + 2 };
  }
}
function drawEastHouse(p, r) {
  const E = EAST, P = E.pal;
  for (let y = E.top; y < GROUND + 2; y++) for (let i = E.x0; i < E.x1; i++) {
    let col;
    if (y < E.top + 12) {
      const u = (y - E.top) / 12;
      col = y === E.top ? '#e6ecf8' : bayer(i, y) < 0.7 - u * 0.6 ? '#c4cae4' : '#9aa2c4';
      if (y === E.top + 11) col = '#3a3448';
    } else {
      let v = 0.3 + 0.42 * (y - E.top) / (GROUND - E.top) + (hash(i, y, 9) - 0.5) * 0.06;
      if (i < E.x0 + 2) v += 0.15;
      col = P[clamp(Math.floor(v * 4 + bayer(i, y) - 0.3), 0, 4)];
    }
    p.put(i, y, col);
  }
  for (let y = E.top + 16; y < GROUND - 8; y += 14) for (let x = E.x0 + 14; x < E.x1 - 4; x += 13) {
    const v = r(), lit = v < 0.36;
    for (let j = 0; j < 7; j++) for (let i = 0; i < 4; i++) p.put(x + i, y + j, lit ? (j < 3 ? '#ffd690' : '#f2ae60') : (j < 2 ? '#2a2842' : '#15131f'));
    for (let i = -1; i <= 4; i++) p.put(x + i, y + 7, hash(x + i, y, 10) < 0.8 ? '#eef2fc' : P[1]);
    if (!lit && v < 0.7) HWIN.push({ id: HWIN.length, x, y, w: 4, h: 7, n: 3 + Math.floor(r() * 5), off: r() });
  }
}

/* ================= Neues Rathaus ================= */
// stone from deep shadow to floodlit highlight; the snow on the roof from shade to lit
const ST = ['#221a2a', '#382a36', '#54403e', '#765848', '#9c7656', '#c49a6c', '#e8c690'];
const SNOWROOF = ['#4c5076', '#686e96', '#8a90b8', '#aeb4d6', '#d2d8ee'];
// The front, left to right: the Wurmeck corner turret, the western tract (a gable, a quiet run under the Frauenkirche,
// the tower bay, a gable), the turret at the join, then the eastern first phase (a gable, the risalit, a gable) and the
// Dienerstraße corner turret. Nothing mirrors about the tower.
const BUTTRESS = [100, 124, 148, 208, 232, JOIN - 1, 274, RIS - RIS_HW, RIS + RIS_HW - 2, 347, 369];   // left edges, 3 px wide
const WIN_X = [106, 112, 118, 130, 136, 142, 154, TX - 19, TX + 15, 214, 220, 226, 238, 244,   // western tract
  256, 262, 268, 280, 286, 292, 298, RIS - 13, RIS - 7, RIS - 1, RIS + 5, RIS + 11, 353, 359, 365, 375, 381, 387];   // eastern part
const SMALL_PIN = [112, 136, 154, 220, 241, 262, 289, 359, 381];   // small pinnacles on the parapet, mid-bay
const GABLES = [112, 220, 262, 381];                 // gable centres, 24 px wide at the parapet
const GABLE_TOP = 91;
const WROWS = [128, 141, 154, 167];                  // facade window rows (7 px tall, pointed)
const LOGGIA_Y = 152;                                // the risalit's loggia on the 2nd floor, in place of the 154 row
const RIS_GABLE = 80;                                // apex of the risalit's big gable
const RWIN = [];                                     // facade windows that switch on and off: { id, x, y, n, off }
const stoneAt = (i, y, v) => ST[clamp(Math.floor(v * 6 + bayer(i, y) - 0.5), 0, 6)];
const ST_OLD = ['#231828', '#3a2733', '#583a3a', '#7a5044', '#9e6a50', '#c28a62', '#e4b684'];   // the eastern first phase
const oldAt = (i, y, v) => ST_OLD[clamp(Math.floor(v * 6 + bayer(i, y) - 0.5), 0, 6)];
// a pointed 3 px window at (x, y): lit, dark, or dark with a mullion
function gothicWindow(p, x, y, lit) {
  for (let j = 0; j < 7; j++) for (let i = 0; i < 3; i++) {
    if (j === 0 && i !== 1) continue;
    let col = lit ? (j < 3 ? '#ffd48c' : '#f2ac5e') : (j < 3 && i === 0 ? '#2e2640' : '#18121f');
    if (i === 1 && j >= 2) col = lit ? '#8a5634' : '#3a2c36';
    p.put(x + i, y + j, col);
  }
}
function renderRathaus() {
  const p = painter(W, H), put = p.put, r = rng(606);
  WEST.forEach(hs => drawHouse(p, hs, r));
  drawEastHouse(p, r);
  // snow-covered roof with tiles showing where the snow slid, two rows of little dormers
  for (let y = RIDGE; y < EAVE; y++) for (let i = RX0 + 4; i <= RX1 - 4; i++) {
    const u = (y - RIDGE) / (EAVE - RIDGE);
    let v = 0.85 - u * 0.45 + (hash(i, 3, 17) - 0.5) * 0.1;
    if (y === RIDGE) v = 1;
    const tile = hash(i >> 1, 4, 17) < 0.13 && u > 0.35 + hash(i >> 1, 5, 17) * 0.3;
    put(i, y, tile ? (bayer(i, y) < 0.5 ? '#383350' : '#48445f') : SNOWROOF[clamp(Math.floor(v * 4 + bayer(i, y) - 0.5), 0, 4)]);
  }
  const clearOfGables = (x, w) => !GABLES.some(g => x + w > g - 14 && x < g + 14)
    && (x + w < TX - 25 || x > TX + 25)                                    // the tower bay
    && (x + w < RIS - RIS_HW - 3 || x > RIS + RIS_HW + 3)                  // the risalit
    && (x + w < JOIN - 3 || x > JOIN + 3);                                 // the turret at the join
  for (let x = RX0 + 10; x < RX1 - 12; x += 16) {
    if (clearOfGables(x, 5)) {                                   // lower dormers (5 px)
      [[2, 2], [1, 3], [0, 4]].forEach(([a, b], k) => { for (let i = a; i <= b; i++) put(x + i, 111 + k, k === 2 && (i === 0 || i === 4) ? '#3a3448' : '#eef2fc'); });
      for (let y = 114; y <= 118; y++) for (let i = 0; i < 5; i++) {
        const edge = i === 0 || i === 4;
        put(x + i, y, edge ? (i === 0 ? '#9c7656' : '#382a36') : hash(x, 1, 19) < 0.35 ? (y < 116 ? '#ffd48c' : '#e8a25a') : (y === 114 ? '#2e2640' : '#18121f'));
      }
    }
    const ux = x + 8;
    if (clearOfGables(ux, 3)) {                                  // upper dormers (3 px)
      put(ux + 1, 105, '#eef2fc'); for (let i = 0; i < 3; i++) put(ux + i, 106, '#dfe6f6');
      for (let y = 107; y <= 109; y++) { put(ux, y, '#765848'); put(ux + 1, y, hash(ux, 2, 19) < 0.3 ? '#ffd48c' : '#18121f'); put(ux + 2, y, '#382a36'); }
    }
  }
  // facade: floodlit from the square, brighter low down; buttresses, string courses, pointed windows
  for (let y = EAVE; y <= GROUND + 4; y++) for (let i = RX0 + 6; i <= RX1 - 7; i++) {
    let v = 0.36 + 0.3 * clamp((y - EAVE) / 62, 0, 1) + (hash(i, y, 11) - 0.5) * 0.06;
    for (const b of BUTTRESS) { const d = i - b; if (d >= 0 && d <= 2) v += d === 0 ? 0.3 : d === 1 ? 0.12 : -0.2; }
    if (i >= TX - 23 && i <= TX + 22) v += 0.06;                                    // the tower bay stands forward
    const dr = i - (RIS - RIS_HW);
    if (dr >= 0 && dr <= 2 * RIS_HW) v += 0.1;                                      // so does the risalit,
    else if (dr > 2 * RIS_HW && dr <= 2 * RIS_HW + 2) v -= 0.2;                     // casting a shadow to its right
    if (y > EAVE + 1 && y < EAVE + 4) v -= 0.1;                                    // shadow under the parapet
    if (y === EAVE || y === 139 || y === 152 || y === 165) v += 0.16;
    else if (y === EAVE + 1 || y === 140 || y === 153 || y === 166) v -= 0.18;
    put(i, y, i > JOIN + 1 ? oldAt(i, y, v) : stoneAt(i, y, v));               // the older part: brick with stone trim
  }
  const winAt = (x, y) => {
    const v = r(), lit = v < 0.24;
    gothicWindow(p, x, y, lit);
    put(x - 1, y + 3, '#c49a6c'); put(x + 3, y + 3, '#382a36');                    // lit jamb / reveal in shadow
    if (!lit && v < 0.52) RWIN.push({ id: RWIN.length, x, y, n: 3 + Math.floor(r() * 6), off: r() });
  };
  for (const y of WROWS) for (const x of WIN_X)
    if (y !== 154 || Math.abs(x + 1 - RIS) > RIS_HW) winAt(x, y);                // the loggia takes the risalit's 2nd floor
  // openwork parapet with snow on top, pinnacles on every buttress and between them
  for (let i = RX0 + 6; i <= RX1 - 7; i++) {
    put(i, 119, hash(i, 1, 13) < 0.75 ? snowPx(hash(i, 2, 13), hash(i, 3, 13)) : '#9c7656');
    put(i, 120, '#c49a6c');
    put(i, 121, (i & 1) ? '#765848' : '#221a2a'); put(i, 122, (i & 1) ? '#765848' : '#2e2232');
    put(i, 123, '#54403e');
  }
  const pinnacle = (x, top, big) => {
    for (let y = top; y <= 123; y++) {
      const base = y >= 116 && big;
      if (base) { put(x - 1, y, '#c49a6c'); put(x, y, '#9c7656'); put(x + 1, y, '#382a36'); }
      else put(x, y, y === top ? '#eef2fc' : (y & 1) ? '#c49a6c' : '#9c7656');
    }
    if (big) { put(x - 1, top + 3, '#e8c690'); put(x + 1, top + 3, '#54403e'); }       // crockets
  };
  // a tall pinnacle turret: crocketed shaft from the parapet up, a spire with a snow tip
  const turretPin = (x, top) => {
    for (let y = top + 7; y <= 123; y++) { put(x - 1, y, '#e8c690'); put(x, y, '#9c7656'); put(x + 1, y, '#382a36'); }
    for (let y = top; y < top + 7; y++) put(x, y, y === top ? '#eef2fc' : (y & 1) ? '#c49a6c' : '#9c7656');
    put(x - 1, top + 4, '#e8c690'); put(x + 1, top + 4, '#54403e'); put(x - 1, top + 6, '#eef2fc');
  };
  for (const b of BUTTRESS) pinnacle(b + 1, 110, true);
  for (const x of SMALL_PIN) pinnacle(x, 115, false);
  // gables: pointed, crocketed copings, a tall tracery window, a finial
  for (const g of GABLES) {
    for (let y = GABLE_TOP; y < EAVE; y++) {
      const hw = Math.round(12 * (y - GABLE_TOP) / (EAVE - GABLE_TOP));
      for (let dx = -hw; dx <= hw; dx++) {
        let col;
        if (dx === -hw) col = '#e8c690';
        else if (dx === hw) col = '#382a36';
        else if (dx === -hw + 1) col = '#c49a6c';
        else col = stoneAt(g + dx, y, 0.46 + 0.2 * (y - GABLE_TOP) / 33 + (dx < 0 ? 0.05 : -0.05));
        put(g + dx, y, col);
      }
      if ((y - GABLE_TOP) % 4 === 2 && hw > 1) { put(g - hw - 1, y, '#e8c690'); put(g + hw + 1, y, '#54403e'); }   // crockets
      if (hash(g, y, 14) < 0.5) put(g - hw, y - 1, '#eef2fc');                                                  // snow on the coping
    }
    for (let y = 99; y <= 117; y++) for (let dx = -1; dx <= 1; dx++) {                 // tall tracery window
      if (y === 99 && dx !== 0) continue;
      put(g + dx, y, dx === 0 && y > 101 ? '#54403e' : y === 108 ? '#54403e' : (y < 104 ? '#2e2640' : '#18121f'));
    }
    for (const ox of [-6, 6]) for (let y = 110; y <= 117; y++) put(g + ox, y, y === 110 ? '#382a36' : '#18121f');
    for (let dx = -2; dx <= 2; dx++) put(g + dx, 118, dx < 0 ? '#eef2fc' : '#c8d2ea');   // snowy sill
    for (let y = GABLE_TOP - 7; y < GABLE_TOP; y++) put(g, y, y === GABLE_TOP - 7 ? '#eef2fc' : '#c49a6c');   // finial
    put(g - 1, GABLE_TOP - 4, '#e8c690'); put(g + 1, GABLE_TOP - 4, '#765848');
    pinnacle(g - 12, 108, true); pinnacle(g + 12, 108, true);
  }
  // corner turrets: octagonal shafts, pointed slate caps with snow on the lit side
  for (const tx of [RX0 + 3, RX1 - 3]) {
    for (let y = 101; y <= GROUND + 4; y++) for (let dx = -3; dx <= 3; dx++) {
      const v = 0.42 + 0.28 * clamp((y - 101) / 80, 0, 1) + (dx <= -2 ? 0.24 : dx >= 2 ? -0.22 : Math.abs(dx) === 1 ? -0.04 : 0);
      put(tx + dx, y, stoneAt(tx + dx, y, v));
    }
    for (const wy of [108, 128, 145, 162]) for (let y = wy; y < wy + 4; y++) put(tx, y, y === wy ? '#382a36' : hash(tx, wy, 15) < 0.4 ? '#ffd48c' : '#18121f');
    for (let dx = -4; dx <= 4; dx++) { put(tx + dx, 101, dx < 0 ? '#e8c690' : '#765848'); put(tx + dx, 100, hash(tx + dx, 1, 16) < 0.8 ? '#eef2fc' : '#c49a6c'); }
    for (let y = 83; y < 100; y++) {
      const hw = Math.round(3.5 * (y - 83) / 16);
      for (let dx = -hw; dx <= hw; dx++) {
        const u = (dx + hw) / (2 * hw + 1);
        put(tx + dx, y, u < 0.34 ? (hash(tx + dx, y, 16) < 0.45 ? '#e6ecf8' : '#6a7a86') : u < 0.7 ? '#3a4a52' : '#1e2a32');
      }
    }
    for (let y = 79; y < 83; y++) put(tx, y, y === 79 ? '#eef2fc' : '#c49a6c');
  }
  // the tower bay above the parapet: walls either side of the tower, crowned by pinnacle turrets
  for (let y = 100; y < EAVE; y++) for (const [a, b] of [[TX - 23, TX - 12], [TX + 12, TX + 22]]) for (let i = a; i <= b; i++) {
    let v = 0.46 + 0.16 * (y - 100) / 24 + (i === a ? 0.26 : i === b ? -0.24 : 0);
    let col = stoneAt(i, y, v);
    if (y === 100) col = hash(i, 5, 18) < 0.8 ? '#eef2fc' : '#c49a6c';
    else if (y === 101) col = '#c49a6c';
    put(i, y, col);
  }
  for (const [x, lit] of [[TX - 18, true], [TX + 16, false]]) for (let y = 106; y <= 117; y++) {
    if (y === 106) { put(x + 1, y, lit ? '#ffd48c' : '#2e2640'); continue; }
    for (let i = 0; i < 3; i++) put(x + i, y, i === 1 && y > 108 ? (lit ? '#8a5634' : '#3a2c36') : lit ? (y < 110 ? '#ffd48c' : '#f2ac5e') : '#18121f');
  }
  for (const x of [TX - 22, TX + 20]) turretPin(x, 85);
  turretPin(JOIN, 89);                                                    // the join of the two building phases
  drawRisalit(p, turretPin);
  drawTower(p);
  return p.done();
}

/* ----- the eastern part's five-axis central risalit ----- */
// corner turrets, a big crocketed gable with the city arms between two angels and a great tracery window, the knight
// with the weather vane on its apex, and the loggia on the 2nd floor: five lit arches, the four civic virtues on the
// columns between them, a balustrade with snow on its rail
const ARMS = ['GGGGG', 'GwkwG', 'GwkwG', 'GwkwG', '.GwG.', '..G..'];
const ARMS_KEY = { G: '#e8b848', w: '#dcd6cc', k: '#1c1a24' };
const ANGEL = ['.w.', 'wWw', '.W.', '.W.'];
const ANGEL_STONE = { w: '#f4e6c4', W: '#c49a6c' };
const KNIGHT = ['.g.', '.h.', 'lHd', 'lHd', '.Hd', '.H.', 'l.d'];
const KNIGHT_KEY = { g: '#e8b848', h: '#9c7656', l: '#c49a6c', H: '#54403e', d: '#221a2a' };
const VIRTUE = ['.h.', 'sSd', '.Sd', '.Sd', 'sSd'];
const VIRTUE_KEY = { h: '#e8c690', s: '#c49a6c', S: '#765848', d: '#382a36' };
function drawRisalit(p, turretPin) {
  const put = p.put, GT = RIS_GABLE, ghw = RIS_HW - 2, rows = EAVE - 1 - GT;
  for (let y = GT; y < EAVE; y++) {                                          // the gable
    const hw = Math.round(ghw * (y - GT) / rows);
    for (let dx = -hw; dx <= hw; dx++) {
      let col;
      if (dx === -hw) col = '#e8c690';
      else if (dx === hw) col = '#382a36';
      else if (dx === -hw + 1) col = '#c49a6c';
      else col = stoneAt(RIS + dx, y, 0.5 + 0.2 * (y - GT) / rows + (dx < 0 ? 0.05 : -0.05));
      put(RIS + dx, y, col);
    }
    if ((y - GT) % 4 === 2 && hw > 1) { put(RIS - hw - 1, y, '#e8c690'); put(RIS + hw + 1, y, '#54403e'); }   // crockets
    if (hash(RIS, y, 34) < 0.55) put(RIS - hw, y - 1, '#eef2fc');                                            // snow on the coping
  }
  stamp(p, ARMS, ARMS_KEY, RIS - 2, GT + 12);                               // the city arms, an angel either side
  stamp(p, ANGEL, ANGEL_STONE, RIS - 7, GT + 17); stamp(p, ANGEL, ANGEL_STONE, RIS + 5, GT + 17);
  for (let dx = -8; dx <= 8; dx++) put(RIS + dx, GT + 22, dx < 0 ? '#c49a6c' : '#765848');   // string course
  const WY = GT + 24, WB = EAVE - 5;                                        // the great tracery window
  for (let y = WY; y <= WB; y++) for (let dx = -3; dx <= 3; dx++) {
    const ly = y - WY, hw = ly === 0 ? 0 : ly === 1 ? 1 : ly === 2 ? 2 : 3;
    if (Math.abs(dx) > hw) continue;
    let col = Math.abs(dx) === hw && ly > 2 ? (dx < 0 ? '#c49a6c' : '#382a36') : (dx & 1) && ly > 4 ? '#54403e' : ly < 7 ? '#2e2640' : '#18121f';
    if (ly === 7 && hw === 3 && Math.abs(dx) < 3) col = '#54403e';           // transom under the tracery head
    put(RIS + dx, y, col);
  }
  put(RIS, WY + 3, '#ffd48c'); put(RIS - 1, WY + 4, '#e8a25a'); put(RIS + 1, WY + 4, '#b8763e');   // a lit rose in the head
  for (let dx = -4; dx <= 4; dx++) put(RIS + dx, WB + 1, dx < 0 ? '#eef2fc' : '#c8d2ea');           // snowy sill
  for (const ox of [-8, 8]) for (let y = WB - 8; y <= WB; y++) put(RIS + ox, y, y === WB - 8 ? '#382a36' : '#18121f');   // side lancets
  // the knight with the weather vane on the apex
  put(RIS - 1, GT - 1, '#e8c690'); put(RIS, GT - 1, '#9c7656'); put(RIS + 1, GT - 1, '#382a36');
  stamp(p, KNIGHT, KNIGHT_KEY, RIS - 1, GT - 8);
  for (let y = GT - 13; y <= GT - 3; y++) put(RIS + 2, y, '#8a6a3a');       // his staff
  put(RIS + 3, GT - 13, '#e8b848'); put(RIS + 4, GT - 13, '#e8b848'); put(RIS + 3, GT - 12, '#c89020');   // the vane
  // corner turrets
  turretPin(RIS - RIS_HW + 1, GT + 3); turretPin(RIS + RIS_HW - 1, GT + 3);
  // the loggia on the 2nd floor
  const LY = LOGGIA_Y;
  for (let i = RIS - 15; i <= RIS + 15; i++) for (let y = LY - 1; y <= LY + 11; y++) put(i, y, stoneAt(i, y, 0.62 + (i === RIS - 15 ? 0.2 : i === RIS + 15 ? -0.3 : 0)));
  for (const o of [-12, -6, 0, 6, 12]) for (let y = LY; y <= LY + 9; y++) {     // five pointed arches, lit inside
    const ly = y - LY, hw = ly === 0 ? 0 : ly === 1 ? 1 : 2;
    for (let dx = -hw; dx <= hw; dx++) put(RIS + o + dx, y, ly < 1 ? '#f6b466' : ly < 4 ? '#ffdc98' : ly < 7 ? '#f6b466' : '#dc8c46');
  }
  for (const o of [-9, -3, 3, 9]) {                                             // columns and the four civic virtues
    for (let y = LY + 1; y <= LY + 9; y++) put(RIS + o, y, '#c49a6c');
    stamp(p, VIRTUE, VIRTUE_KEY, RIS + o - 1, LY + 2);
  }
  for (let i = RIS - 14; i <= RIS + 14; i++) {                                   // balustrade
    put(i, LY + 7, hash(i, 1, 35) < 0.7 ? snowPx(hash(i, 2, 35), hash(i, 3, 35)) : '#e8c690');
    put(i, LY + 8, '#c49a6c');
    put(i, LY + 9, (i & 1) ? '#765848' : '#e89a52');
    put(i, LY + 10, '#54403e');
  }
}

/* ----- the tower: square shaft, clock, gallery, octagonal lantern, spire, the Münchner Kindl on top ----- */
function drawTower(p) {
  const put = p.put;
  for (let y = 57; y <= GROUND + 4; y++) for (let i = TX - 11; i <= TX + 11; i++) {
    const d = i - (TX - 11);
    let v = 0.5 + 0.22 * clamp((y - 57) / 70, 0, 1) + (hash(i, y, 21) - 0.5) * 0.05;
    if (d <= 1) v += 0.3; else if (d === 2) v += 0.12; else if (d >= 21) v -= 0.3; else if (d === 20) v -= 0.12;
    if (d === 5 || d === 17) v -= 0.14;                                  // recessed panels
    else if (d === 6 || d === 18) v += 0.06;
    if (y === 60 || y === 75 || y === 88) v += 0.22; else if (y === 61 || y === 76 || y === 89) v -= 0.2;
    put(i, y, stoneAt(i, y, v));
  }
  for (const x of [TX - 4, TX + 3]) for (let y = 71; y <= 86; y++) for (let i = 0; i < 2; i++)   // paired lancets
    put(x + i, y, y === 71 || y === 79 ? '#54403e' : i === 0 && (y < 74 || (y > 79 && y < 82)) ? '#2e2640' : '#18121f');
  for (let i = TX - 11; i <= TX + 11; i++) for (const y of [59, 74, 87]) if (hash(i, y, 22) < 0.6) put(i, y, snowPx(hash(i, y, 23), hash(i, y, 24)));
  // clock face (the hands show Munich's time, drawn per frame in drawTowerLife; nine, the hour of the evening show, at rest)
  for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {
    const d = Math.hypot(dx, dy);
    if (d <= 4.3) put(CLOCK.x + dx, CLOCK.y + dy, d > 3.3 ? (dx + dy < 0 ? '#c49a6c' : '#6a4a2c') : '#f8ecc4');
  }
  // gallery and balustrade, snow on the coping
  for (let i = TX - 14; i <= TX + 14; i++) {
    put(i, 57, '#382a36'); put(i, 56, i < TX ? '#e8c690' : '#c49a6c');
    put(i, 55, (i & 1) ? '#9c7656' : '#221a2a'); put(i, 54, i < TX - 6 ? '#e8c690' : '#c49a6c');
    if (hash(i, 1, 25) < 0.7) put(i, 53, snowPx(hash(i, 2, 25), hash(i, 3, 25)));
  }
  // octagonal lantern with three open arches (their warm glow is drawn per frame)
  for (let y = 44; y <= 52; y++) for (let i = TX - 7; i <= TX + 7; i++) {
    const d = i - (TX - 7);
    put(i, y, stoneAt(i, y, 0.52 + (d <= 1 ? 0.3 : d >= 13 ? -0.3 : Math.abs(d - 7) === 3 ? -0.08 : 0)));
  }
  for (const [a, b] of [[TX - 5, TX - 4], [TX - 1, TX + 1], [TX + 4, TX + 5]]) for (let y = 46; y <= 52; y++) for (let i = a; i <= b; i++)
    if (y > 46 || (b - a === 2 && i === a + 1)) put(i, y, '#140e18');
  for (let i = TX - 8; i <= TX + 8; i++) put(i, 43, i < TX ? '#e8c690' : '#9c7656');
  // four corner pinnacles on the gallery
  for (const x of [TX - 13, TX + 13]) {
    for (let y = 44; y <= 53; y++) { put(x - 1, y, '#e8c690'); put(x, y, '#9c7656'); put(x + 1, y, '#382a36'); }
    for (let y = 35; y < 44; y++) put(x, y, y === 35 ? '#eef2fc' : (y & 1) ? '#c49a6c' : '#765848');
    put(x - 1, 40, '#e8c690'); put(x + 1, 40, '#54403e'); put(x - 1, 43, '#eef2fc');
  }
  // the spire: slate and copper, crockets up both edges, snow streaks on the lit side
  for (let y = 18; y <= 42; y++) {
    const hw = Math.round(7 * (y - 17) / 25);
    for (let dx = -hw; dx <= hw; dx++) {
      const u = (dx + hw) / (2 * hw + 1);
      let col = u < 0.3 ? '#6e8c80' : u < 0.62 ? '#3c5452' : '#1e2c30';
      if (u < 0.42 && hash(dx, y, 26) < 0.2) col = '#dfe6f4';
      put(TX + dx, y, col);
    }
    if (y % 4 === 1 && hw > 0) { put(TX - hw - 1, y, '#a8c0b0'); put(TX + hw + 1, y, '#2a3a3c'); }
  }
  // the Münchner Kindl on its rod
  for (let y = 14; y < 18; y++) put(TX, y, '#c8a050');
  put(TX, 10, '#ffe8a0'); put(TX, 11, '#d8a040'); put(TX + 1, 11, '#6a4418'); put(TX, 12, '#8a5a20'); put(TX + 1, 12, '#4a3014');
  put(TX, 13, '#6a4418');
  drawOrielFrame(p);
}

/* ----- the Glockenspiel oriel: copper canopy, carillon bells, two arcaded tiers on a corbel ----- */
// the arched opening of a tier (first row y0): is (i, y) inside it?
function orielOpen(i, y, y0) {
  const ly = y - y0;
  if (ly < 0 || ly > 9) return false;
  if (ly === 0) return i >= TX - 2 && i <= TX + 2;
  if (ly === 1) return i >= TX - 4 && i <= TX + 4;
  return i >= TX - 5 && i <= TX + 5;
}
function drawOrielFrame(p) {
  const put = p.put, { x0, x1, up, lo } = ORIEL;
  [0, 1, 2, 4, 6, 7].forEach((hw, k) => {                                  // canopy, rows up-9 .. up-4
    const y = up - 9 + k;
    for (let dx = -hw; dx <= hw; dx++) put(TX + dx, y, dx < -hw / 3 ? '#8ad0aa' : dx < hw / 3 ? '#52a07e' : '#2f6c58');
    if (hw > 0 && hash(k, 1, 27) < 0.8) put(TX - hw, y - 1, '#eef2fc');
  });
  put(TX, up - 10, '#e8c690'); put(TX, up - 11, '#fff0b0');
  for (let i = x0; i <= x1; i++) {                                         // bell frame: beam, bells, shadow
    const bell = (i - x0) % 2 === 1;
    put(i, up - 3, '#382a36'); put(i, up - 2, bell ? '#e8b848' : '#221a2a'); put(i, up - 1, bell ? '#8a5a1a' : '#221a2a');
  }
  for (const y0 of [up, lo]) {                                             // the two tiers: jambs, arches, closed shutters
    for (let y = y0; y <= y0 + 9; y++) for (let i = x0; i <= x1; i++) {
      let col;
      if (orielOpen(i, y, y0)) {
        const plank = (i - TX + 6) % 3 === 0;
        col = i === TX ? '#6a4020' : plank ? '#24140c' : '#3a2416';          // closed shutters, a warm seam
      } else col = i <= x0 + 1 ? (i === x0 ? '#e8c690' : '#c49a6c') : i >= x1 - 1 ? (i === x1 ? '#2a2030' : '#54403e') : stoneAt(i, y, 0.62);
      put(i, y, col);
    }
  }
  for (let i = x0; i <= x1; i++) {                                         // balcony parapet between the tiers
    put(i, up + 10, i < TX ? '#e8c690' : '#c49a6c');
    put(i, up + 11, (i & 1) ? '#9c7656' : '#221a2a');
    put(i, up + 12, '#382a36');
    put(i, up + 13, '#765848');
    if (hash(i, 2, 27) < 0.7) put(i, up + 9, '#eef2fc');
  }
  [15, 13, 11, 9, 7, 5, 3].forEach((w, k) => {                              // corbel
    const y = lo + 10 + k, hw = w >> 1;
    for (let dx = -hw; dx <= hw; dx++) put(TX + dx, y, k === 0 ? (dx < 0 ? '#e8c690' : '#c49a6c') : stoneAt(TX + dx, y, 0.6 + (dx < 0 ? 0.12 : -0.18) - k * 0.04));
  });
}

/* ================= Mariensäule: the column with the golden Virgin ================= */
const STATUE = [
  '.g.g.g.',
  '..ggg..',
  '..hGd..',
  '.GGGgd.',
  'hGGGgdd',
  '.GGGgd.',
  '..GGgd.',
  '..GGgd.',
  '.GGGggd',
  'mm...mm',
  '.mmmmm.',
];
const STATUE_KEY = { g: '#e8b440', G: '#ffd86a', h: '#fff4c0', d: '#a06a1e', m: '#c8922a' };
function renderColumn(p) {
  const { x, top } = MS;
  stamp(p, STATUE, STATUE_KEY, x - 3, top);
  const cap = top + STATUE.length;
  for (let dx = -3; dx <= 3; dx++) { p.put(x + dx, cap, dx < 0 ? '#eef2fc' : '#c8d2ea'); p.put(x + dx, cap + 1, dx < 0 ? '#c8a488' : '#6a4a48'); }
  for (let dx = -2; dx <= 2; dx++) p.put(x + dx, cap + 2, dx < 0 ? '#a88070' : '#553a3a');
  for (let y = cap + 3; y < 166; y++) {                                    // red marble shaft
    p.put(x - 1, y, '#c09080'); p.put(x, y, (y & 3) === 0 ? '#7a5450' : '#8e625a'); p.put(x + 1, y, '#4e3434');
  }
  for (let y = 166; y < GROUND + 2; y++) for (let dx = -7; dx <= 7; dx++) {  // pedestal with a cornice and corner putti
    const w = y < 170 ? 5 : y < 176 ? 6 : 7;
    if (Math.abs(dx) > w) continue;
    let col = dx <= -w + 1 ? '#b8a090' : dx >= w - 1 ? '#3e3438' : '#7e6c68';
    if (y === 166 || y === 170 || y === 176) col = dx < 0 ? '#eef2fc' : '#c8d2ea';
    p.put(x + dx, y, col);
  }
  for (const dx of [-6, 6]) { p.put(x + dx, 173, '#3a3034'); p.put(x + dx, 174, '#4a3e40'); p.put(x + dx - 1, 175, '#3a3034'); p.put(x + dx + 1, 175, '#3a3034'); }
  for (let dx = -12; dx <= 12; dx++) {                                     // low balustrade round the base
    p.put(x + dx, GROUND - 3, hash(dx, 1, 33) < 0.8 ? '#eef2fc' : '#9aa2c4');
    p.put(x + dx, GROUND - 2, '#6a5a58');
    if ((dx & 1) === 0) { p.put(x + dx, GROUND - 1, '#5a4c4c'); p.put(x + dx, GROUND, '#5a4c4c'); }
  }
}

/* ================= The Christmas tree ================= */
const TREEC = ['#07130e', '#0c1f15', '#12301d', '#1a4027', '#265232', '#35683e'];
const TIERS = 8;
function treeW(y) {                                   // half-width of the tree at row y, and the tier position there
  const { top, base, hw } = TREE, u = (y - top) / (base - top);
  const k = Math.min(TIERS - 1, Math.floor(u * TIERS)), tu = u * TIERS - k;
  return { w: hw * (0.08 + 0.92 * u) * (0.74 + 0.26 * tu), tu, k };
}
const TREE_LIGHTS = [];                               // { x, y, c } bulbs along drooping swags
function renderTree(p) {
  const { x, top, base } = TREE;
  for (let y = top + 3; y <= base; y++) {
    const { w, tu, k } = treeW(y);
    for (let i = Math.floor(x - w - 2); i <= Math.ceil(x + w + 2); i++) {
      const d = (i - x) / w, jag = tu > 0.72 ? (hash(i, k, 71) - 0.5) * 3 : 0;
      if (Math.abs(i - x) > w + jag) continue;
      let v = 0.22 + 0.5 * tu + (d < -0.35 ? 0.14 : d > 0.45 ? -0.12 : 0) + (hash(i, y, 72) - 0.5) * 0.25;
      let col = TREEC[clamp(Math.floor(v * 5 + bayer(i, y) - 0.5), 0, 5)];
      if (tu > 0.4 && hash(i, y, 73) < 0.2 * tu && d < 0.6) col = hash(i, y, 74) < 0.6 ? '#dfe6f4' : '#a6b2d4';   // snow on the boughs
      if (tu > 0.9 && hash(i, k, 75) < 0.55) col = '#c8d2ea';
      p.put(i, y, col);
    }
  }
  const r = rng(77);
  for (let y0 = top + 12; y0 < base - 6; y0 += 9) {                        // swags of bulbs
    const { w } = treeW(y0 + 4), n = Math.max(2, Math.round(w / 7));
    for (let s = 0; s < n; s++) {
      const a = x - w + (2 * w) * s / n, b = x - w + (2 * w) * (s + 1) / n;
      for (let xx = a + 1; xx < b; xx += 3) {
        const f = (xx - a) / (b - a), yy = Math.round(y0 + Math.sin(Math.PI * f) * 3.5 + (xx - x) * 0.05);
        if (Math.abs(xx - x) > treeW(yy).w - 1) continue;
        TREE_LIGHTS.push({ x: Math.round(xx), y: yy, c: r() < 0.72 ? 0 : r() < 0.7 ? 1 : 2, ph: r() });
      }
    }
  }
}

/* ================= The square: snow on the cobbles, trodden grey where people walk, warm pools by the stalls ================= */
// warm light on the snow: [cx, cy, rx, ry, colour, strength]
const GPOOLS = [
  [24, 233, 44, 11, '#ffb466', 0.6], [159, 233, 60, 13, '#ffb466', 0.65], [233, 235, 38, 10, '#ffb466', 0.6],
  [349, 233, 54, 12, '#ffb466', 0.6], [455, 234, 40, 10, '#ffb466', 0.55], [78, 236, 26, 8, '#ff8a3a', 0.55],
  [TREE.x, 196, 60, 8, '#ffd08a', 0.35], [MS.x, 190, 22, 4, '#ffc878', 0.3], [85, 193, 30, 4, '#ffb466', 0.35],
  [284, 193, 30, 4, '#ffb466', 0.35], [402, 193, 20, 4, '#ffb466', 0.35], [106, 250, 18, 5, '#ffc070', 0.5],
  [292, 252, 18, 5, '#ffc070', 0.5],
  [26, 245, 52, 8, '#ffb466', 0.32], [159, 246, 76, 9, '#ffb466', 0.34], [233, 246, 44, 8, '#ffb466', 0.3],   // the stalls'
  [349, 246, 62, 9, '#ffb466', 0.3],                                                                          // light on the path
];
// ice in the snow glinting in the stalls' light, a few at a time: { x, y, k, ph }
const SNOW_GLINTS = [];
{ const r = rng(91); while (SNOW_GLINTS.length < 26) {
  const k = Math.floor(r() * 5), [cx, cy, rx, ry] = GPOOLS[[0, 1, 2, 3, 5][k]], a = r() * TAU, d = Math.sqrt(r());
  const x = Math.round(cx + Math.cos(a) * d * rx), y = Math.round(cy + 4 + Math.sin(a) * d * (ry + 4));
  if (y > GROUND + 44 && y < H - 4 && x < 348) SNOW_GLINTS.push({ x, y, k: 7 + Math.floor(r() * 9), ph: r() });
} }
function pooled(i, y, col) {
  for (const [cx, cy, rx, ry, lc, s] of GPOOLS) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * s * 5 + bayer(i, y)) / 5;
    if (q > 0) col = tint(col, lc, Math.min(0.65, q * 0.75));
  }
  return col;
}
const FRESH = ['#767c9e', '#959bbc', '#b4bad8', '#d4daee', '#eef2fc'];
// trodden snow, from wet slush in the ruts to packed snow at the path's edges, and the cobbles where it has worn through
const TROD = ['#56586f', '#65677f', '#75778f', '#8789a3', '#9b9eb8'];
const COBBLE = { joint: '#47495f', stone: '#5e6079', wet: '#8486a2' };
const pathMid = i => AISLE + 1 + 5 * Math.sin(i * 0.031) + 3 * Math.sin(i * 0.074 + 1);
const pathHalf = i => 10 + 3 * Math.sin(i * 0.052 + 2);
const worn = (i, y) => Math.sin(i * 0.043 + 0.8) * Math.sin(i * 0.017 + y * 0.09 - 1.1);   // > 0.5: cobbles show
const groove = i => SLED.y + Math.round(Math.sin(i * 0.021 + 0.4));            // the sledge's runner tracks
function renderGround(p) {
  for (let y = GROUND; y < H; y++) {
    const seg = 4 + Math.floor(hash(y, 1, 81) * 9), off = Math.floor(hash(y, 2, 81) * seg);   // each row: ruts a few px long
    for (let i = 0; i < W; i++) {
      const u = (y - GROUND) / (H - GROUND), d = Math.abs(y - pathMid(i)) / pathHalf(i);
      let col;
      if (d < 1 - bayer(i, y) * 0.25) {                                      // the trodden path: slush in the ruts, packed edges
        const v = 0.34 + 0.5 * d * d + 0.1 * u + (hash(Math.floor((i + off) / seg), y, 82) - 0.5) * 0.24;
        col = TROD[clamp(Math.floor(v * 4 + bayer(i, y) - 0.5), 0, 4)];
        if (d < 0.6 && worn(i, y) > 0.5 + d * 0.4 + (bayer(i, y) - 0.5) * 0.2) {    // cobbles through the slush
          const row = (y - GROUND) >> 1, joint = ((i + (row & 1) * 2) & 3) === 0;
          col = joint ? COBBLE.joint : ((y - GROUND) & 1) === 0 ? COBBLE.wet : COBBLE.stone;
        }
      } else {                                                                // fresh snow, deeper towards us
        const v = 0.3 + 0.55 * u + 0.05 * Math.sin(i * 0.09 + y * 0.21) * Math.sin(i * 0.023 - y * 0.05) + (hash(i, y, 85) - 0.5) * 0.12;
        col = FRESH[clamp(Math.floor(v * 4 + bayer(i, y) - 0.5), 0, 4)];
        if ((y === groove(i) || y === groove(i) + 2) && hash(i >> 1, y, 86) < 0.85) col = FRESH[1];
        else if (hash(i, y, 88) < 0.006) col = '#ffffff';                     // a glint
      }
      p.put(i, y, pooled(i, y, col));
    }
  }
  const r = rng(89);                                                          // trails of footprints across the fresh snow
  for (let k = 0; k < 14; k++) {
    let x = r() * W, y = GROUND + 8 + r() * (H - GROUND - 12);
    const dx = (r() < 0.5 ? -1 : 1) * (2 + r() * 2), dy = (r() - 0.5) * 1.2;
    for (let s = 0; s < 16; s++, x += dx, y += dy) {
      const yy = Math.round(y + ((s & 1) ? 1 : 0));
      if (Math.abs(yy - pathMid(Math.round(x))) < pathHalf(Math.round(x)) + 2) continue;
      p.put(x, yy, pooled(x, yy, yy > 236 ? '#8e94b4' : '#7a80a0'));
      if (yy > 244) p.put(x + 1, yy, pooled(x + 1, yy, '#a2a8c6'));
    }
  }
  const r2 = rng(90);                                                         // and boot prints along the path, both ways
  for (let k = 0; k < 40; k++) {
    let x = Math.round(r2() * W);
    const y0 = pathMid(x) + (r2() - 0.5) * pathHalf(x) * 1.5, dir = r2() < 0.5 ? -1 : 1, n = 5 + Math.floor(r2() * 10);
    for (let s = 0; s < n; s++, x += dir * 4) {
      const yy = Math.round(y0 + ((s & 1) ? 1 : 0)), d = Math.abs(yy - pathMid(x)) / pathHalf(x);
      if (d > 1) continue;
      p.put(x, yy, pooled(x, yy, TROD[0]));
      if (yy > 246) p.put(x + 1, yy, pooled(x + 1, yy, TROD[1]));
    }
  }
}

/* ================= Market stalls ================= */
// front view of a wooden hut: gable roof with thick snow on red boards, a garland of fir and bulbs along the eave, a
// lit opening full of goods, a counter and a painted front. Bulb positions are collected for the per-frame twinkle.
const BULBS_BACK = [], BULBS_FRONT = [], STRING_BULBS = [];   // { x, y, ph } of every garland's and light string's bulbs
const WOOD = ['#1e120c', '#2e1c12', '#46291a', '#643a22', '#8a5230', '#b8763e'];
function drawStall(p, s, bulbs) {
  const { x0, x1, peak, eave, counter, foot, kind } = s, xc = (x0 + x1) / 2, hw = (x1 - x0) / 2 + 3, big = x1 - x0 > 40;
  const snowT = big ? 4 : 3, put = p.put;
  // roof
  for (let i = Math.floor(x0 - 3); i <= Math.ceil(x1 + 3); i++) {
    const top = Math.round(eave - (1 - Math.abs(i - xc) / hw) * (eave - peak)) - (hash(i >> 1, x0, 41) < 0.4 ? 1 : 0);
    for (let y = top; y < eave; y++) {
      const k = y - top;
      let col;
      if (k < snowT) col = k === 0 ? '#f2f6ff' : snowPx(hash(i, y, 42) * 0.9, hash(i, y, 43));
      else if (k < snowT + 2) col = k === snowT ? '#c83a30' : '#8a2420';
      else col = (Math.round(i - x0) % 4 === 0) ? WOOD[0] : WOOD[1];          // pediment planks
      put(i, y, col);
    }
  }
  for (const [ex, dir] of [[x0 - 3, -1], [x1 + 3, 1]]) {                      // icicles at the eaves
    const len = 1 + Math.floor(hash(ex, 1, 44) * 3);
    for (let k = 0; k < len; k++) put(ex - dir, eave + k, k === len - 1 ? '#8ea4c8' : '#dfe8f6');
  }
  // eave: fir garland with bulbs
  for (let i = x0 - 2; i <= x1 + 2; i++) {
    const b = hash(i, x0, 45);
    put(i, eave, b < 0.5 ? '#1c4a2a' : '#123a20');
    put(i, eave + 1, b < 0.3 ? '#2c6a3c' : '#1a5030');
    if (hash(i, x0, 46) < 0.6) put(i, eave + 2, '#10301a');
    if ((i - x0) % 3 === 1) bulbs.push({ x: i, y: eave + 1 + ((i - x0) % 6 === 1 ? 1 : 0), ph: hash(i, x0, 47) });
  }
  // lit opening
  for (let y = eave + 3; y < counter; y++) for (let i = x0 + 2; i <= x1 - 2; i++) {
    const v = (y - eave - 3) / Math.max(1, counter - eave - 4) + (bayer(i, y) - 0.5) * 0.35;
    put(i, y, v < 0.3 ? '#ffe2a2' : v < 0.66 ? '#f8c474' : '#e8a054');
  }
  drawGoods(p, s);
  // posts, counter, painted front
  for (let y = eave + 1; y <= foot; y++) for (const [i, col] of [[x0, WOOD[4]], [x0 + 1, WOOD[2]], [x1 - 1, WOOD[2]], [x1, WOOD[0]]]) put(i, y, col);
  for (let i = x0 - 1; i <= x1 + 1; i++) {
    put(i, counter, WOOD[5]); put(i, counter + 1, WOOD[3]);
    if (hash(i, x0, 48) < 0.35) put(i, counter - 1, '#eef2fc');
  }
  for (let y = counter + 2; y <= foot; y++) for (let i = x0 + 2; i <= x1 - 2; i++) {
    const board = Math.floor((i - x0 - 2) / 4) & 1, v = (y - counter) / (foot - counter);
    let col = board ? (v < 0.5 ? '#a8302a' : '#8e2824') : (v < 0.5 ? '#c43c30' : '#a8322a');
    if ((i - x0 - 2) % 4 === 3) col = '#5a1a18';
    if (y === counter + 2) col = '#3a1410';
    put(i, y, col);
  }
  for (let i = x0 - 1; i <= x1 + 1; i++) if (hash(i, x0, 49) < 0.7) put(i, foot + 1, snowPx(hash(i, x0, 50), 0.6));   // drift at the foot
}
// what each stall sells, drawn into its lit opening
function drawGoods(p, s) {
  const { x0, x1, eave, counter, kind } = s, put = p.put, y0 = eave + 3, h = counter - y0;
  const r = rng(x0 * 7 + 3);
  if (kind === 'candles') {
    for (const sy of [y0 + Math.round(h * 0.45), counter - 1]) {
      for (let i = x0 + 3; i < x1 - 2; i++) put(i, sy, WOOD[3]);
      for (let i = x0 + 4; i < x1 - 3; i += 3) {
        const hgt = 2 + Math.floor(r() * 3), col = ['#f4ecd8', '#c83a30', '#e8c860', '#3a6aa8'][Math.floor(r() * 4)];
        for (let k = 1; k <= hgt; k++) put(i, sy - k, col);
        put(i, sy - hgt - 1, '#fff6c0');
      }
    }
  } else if (kind === 'pretzels') {
    for (const ry of [y0 + 1, y0 + Math.round(h * 0.5)]) {
      for (let i = x0 + 3; i < x1 - 2; i++) put(i, ry, WOOD[2]);
      for (let i = x0 + 5; i < x1 - 6; i += 7) stamp(p, ['.b.b.', 'bBbBb', 'b.b.b', '.bbb.'], { b: '#9a5224', B: '#d89048' }, i, ry + 1);
    }
    for (let i = x0 + 4; i < x1 - 4; i += 5) stamp(p, ['.bb.', 'bBBb', '.bb.'], { b: '#8a4a20', B: '#c8843e' }, i, counter - 3);
  } else if (kind === 'mugs') {
    for (const sy of [y0 + Math.round(h * 0.4), counter - 1]) {
      for (let i = x0 + 3; i < x1 - 2; i++) put(i, sy, WOOD[3]);
      for (let i = x0 + 4; i < x1 - 6; i += 6) {
        const col = ['#b02a36', '#2a4a9a', '#e8e0d0', '#2a7a4a'][Math.floor(r() * 4)];
        for (let k = 1; k <= 3; k++) for (let j = 0; j < 3; j++) put(i + j, sy - k, k === 3 ? tint(col, '#ffffff', 0.35) : j === 2 ? tint(col, '#000000', 0.3) : col);
        put(i + 3, sy - 2, col); put(i + 1, sy - 2, tint(col, '#ffffff', 0.5));
      }
    }
  } else if (kind === 'ornaments') {
    for (let row = 0; row < 3; row++) {
      const ry = y0 + 2 + row * Math.max(3, Math.floor(h / 3.2));
      for (let i = x0 + 4 + (row & 1) * 2; i < x1 - 3; i += 4) {
        const col = ['#e83a3a', '#e8c040', '#3a7aff', '#d8dce8', '#b03ad8', '#3ac86a'][Math.floor(r() * 6)];
        put(i, ry - 1, '#8a7a5a'); put(i, ry, col); put(i + 1, ry, col); put(i, ry + 1, col); put(i + 1, ry + 1, tint(col, '#000000', 0.35));
        put(i, ry, '#ffffff');
      }
    }
  } else if (kind === 'nutcrackers') {
    for (let i = x0 + 4; i < x1 - 4; i += 5) stamp(p, ['.k.', 'kkk', '.f.', 'rrr', 'rYr', 'k.k'], { k: '#1a1418', f: '#e8b890', r: r() < 0.5 ? '#c42a2a' : '#2a4aa8', Y: '#e8c040' }, i, counter - 6);
    for (let i = x0 + 3; i < x1 - 2; i += 3) { put(i, y0 + 1, '#e8c040'); put(i, y0 + 2, '#c89020'); }
  } else if (kind === 'hearts') {
    for (let i = x0 + 4; i < x1 - 5; i += 6) {
      for (let k = 0; k < 2; k++) put(i + 2, y0 + k, '#c83a30');
      stamp(p, ['b.b', 'bbb', '.b.'], { b: r() < 0.5 ? '#8a4a24' : '#a85a2a' }, i + 1, y0 + 2);
      put(i + 1, y0 + 2, '#f4f0e8');
    }
  } else if (kind === 'grill') {
    for (let i = x0 + 4; i < x1 - 3; i++) { put(i, counter - 3, (i & 1) ? '#ff8a2a' : '#ffc050'); put(i, counter - 2, '#3a1a10'); }
    for (let i = x0 + 5; i < x1 - 5; i += 3) { put(i, counter - 4, '#b8603a'); put(i + 1, counter - 4, '#8a4028'); }
  }
}
// back row (far, small) and front row (near, big): { x0, x1, peak, eave, counter, foot, kind, sign? }
const BACK_STALLS = [[-4, 22], [26, 52], [56, 84], [98, 124], [128, 152], [156, 182], [186, 212], [216, 242], [246, 272],
  [308, 334], [338, 364], [368, 394], [400, 426], [430, 456], [460, 488]].map(([x0, x1], k) => ({
  x0, x1, peak: 166 + (k % 3), eave: 175, counter: 184, foot: 190,
  kind: ['mugs', 'ornaments', 'candles', 'hearts', 'grill', 'nutcrackers', 'pretzels'][k % 7],
}));
const FRONT_STALLS = [
  { x0: -6, x1: 52, peak: 180, eave: 197, counter: 214, foot: FGND, kind: 'candles', sign: 'KERZEN' },
  { x0: 118, x1: 200, peak: 172, eave: 192, counter: 213, foot: FGND, kind: 'pretzels' },
  { x0: 208, x1: 258, peak: 182, eave: 198, counter: 215, foot: FGND, kind: 'mugs', sign: 'GLÜHWEIN' },
  { x0: 310, x1: 388, peak: 178, eave: 196, counter: 214, foot: FGND, kind: 'ornaments', sign: 'SCHMUCK' },
  { x0: 418, x1: 492, peak: 181, eave: 198, counter: 215, foot: FGND, kind: 'hearts', sign: 'LEBKUCHEN' },
];
function renderBack() {
  const p = painter(W, H);
  for (const s of BACK_STALLS) drawStall(p, s, BULBS_BACK);
  return p.done();
}
function renderFront() {
  const p = painter(W, H);
  for (const s of FRONT_STALLS) drawStall(p, s, BULBS_FRONT);
  // pediment boards: a sign, or the bakery's pretzel emblem
  const boards = [];
  for (const s of FRONT_STALLS) {
    const xc = Math.round((s.x0 + s.x1) / 2), by = s.eave - 9;
    if (s.sign) {
      const w = signWidth(s.sign) + 4;
      for (let y = by; y < by + 8; y++) for (let i = xc - (w >> 1); i < xc - (w >> 1) + w; i++)
        p.put(i, y, y === by || y === by + 7 || i === xc - (w >> 1) || i === xc - (w >> 1) + w - 1 ? '#5a3418' : '#f4e2b4');
      boards.push([s.sign, xc - (w >> 1) + 2, by + 2]);
    } else {
      stamp(p, ['..bbb.bbb..', '.b...b...b.', 'b....b....b', 'b...b.b...b', '.b.b...b.b.', '..b.....b..'],
        { b: '#e8a050' }, xc - 5, by);
    }
  }
  drawRoaster(p);
  // strings of lights sagging from one stall's gable to the next
  for (let k = 0; k + 1 < FRONT_STALLS.length; k++) {
    const A = FRONT_STALLS[k], B = FRONT_STALLS[k + 1];
    const ax = Math.round((A.x0 + A.x1) / 2), bx = Math.round((B.x0 + B.x1) / 2), ay = A.peak - 1, by = B.peak - 1;
    const sag = 6 + (bx - ax) * 0.05;
    let prev = null;
    for (let x = ax; x <= bx; x++) {
      const f = (x - ax) / (bx - ax), y = Math.round(lerp(ay, by, f) + sag * Math.sin(Math.PI * f));
      if (prev !== null) for (let yy = Math.min(prev, y); yy <= Math.max(prev, y); yy++) p.put(x, yy, '#241c1c');
      else p.put(x, y, '#241c1c');
      if ((x - ax) % 4 === 2) STRING_BULBS.push({ x, y: y + 1, ph: hash(x, k, 52) });
      prev = y;
    }
  }
  const c = p.done();
  for (const [s, x, y] of boards) sign(p.x, s, x, y, '#8a2a1e');
  text(p.x, 'MARONI', ROAST.x + 3, ROAST.y + 13, '#f4dca0');
  return c;
}

/* ================= Chestnut roaster ================= */
function drawRoaster(p) {
  const { x, y } = ROAST, put = p.put;
  for (let j = 0; j < 20; j++) for (let i = 0; i < 28; i++) {             // iron stove on a cart
    let col = i === 0 ? '#5a5660' : i === 27 ? '#0e0c12' : j === 0 ? '#6a6670' : (i + j) % 7 === 0 ? '#2a2830' : '#1e1c24';
    if (j >= 11 && j <= 18 && i >= 1 && i <= 26) col = j === 11 || j === 18 || i === 1 || i === 26 ? '#3a1e14' : '#7a2a1a';   // name plate
    put(x + i, y + j, col);
  }
  for (let i = 4; i < 20; i++) for (let j = 3; j < 9; j++) put(x + i, y + j, (i & 1) && j > 3 ? '#2a1008' : '#141016');   // fire grate (embers per frame)
  for (let i = -2; i < 30; i++) { put(x + i, y - 1, '#4a4650'); put(x + i, y - 2, '#6a6670'); }   // roasting pan rim
  for (let i = 1; i < 26; i += 2) { put(x + i, y - 3, hash(i, 1, 51) < 0.5 ? '#6a3a1a' : '#8a4a22'); if (hash(i, 2, 51) < 0.4) put(x + i + 1, y - 4, '#5a2e14'); }   // chestnuts
  for (let yy = y - 16; yy < y; yy++) { put(x + 24, yy, '#3a3640'); put(x + 25, yy, '#1a1820'); }   // chimney pipe
  for (let i = 23; i <= 26; i++) put(x + i, y - 17, '#4a4650');
  put(x + 23, y - 18, '#eef2fc'); put(x + 24, y - 18, '#eef2fc'); put(x + 25, y - 18, '#c8d2ea');
  for (const wx of [x + 4, x + 22]) stamp(p, ['.sss.', 'smmms', 'smkms', 'smmms', '.sss.'], { s: '#0e0c12', m: '#5a5660', k: '#2a2830' }, wx - 2, y + 19);
  for (let i = -3; i < 31; i++) if (hash(i, 3, 51) < 0.5) put(x + i, y - 3 - (i < 0 || i > 27 ? 0 : 1), '#eef2fc');   // snow on the pan rim ends
}

/* ================= People in hats ================= */
const HATS = {
  bobble: ['...o...', '..hhh..', '.hHhHh.'],
  felt: ['....o..', '..hhh..', 'hhhhhhh'],
  fur: ['.......', '.hhhhh.', '.hhhhh.'],
  beanie: ['.......', '..hhh..', '.hhhhh.'],
};
const BODY_SIDE = ['..aff..', '..aff..', '..sss..', '.sccCc.', '.cccCc.', '.cccCc.', '.cccCc.', '.ccccc.', '..cccc.', '..ccc..'];
const BODY_BACK = ['..aaa..', '..aaa..', '..sss..', '.ccccc.', '.ccccc.', '.cCcCc.', '.cCcCc.', '.ccccc.', '.ccccc.', '..ccc..'];
const LEGS = [['..l.l..', '..l.l..', '.l...l.', '.k...k.'], ['...ll..', '...ll..', '...ll..', '...kk..'], ['..l.l..', '..l.l..', '..l.l..', '..k.k..']];
const STYLES = [
  { hat: 'bobble', h: '#c8303a', o: '#f4f0e8', H: '#f4f0e8', c: '#2a3456', s: '#e8c040', f: '#e8b890', a: '#5a3a22', l: '#1e2230' },
  { hat: 'felt', h: '#2e4a32', o: '#e8e0c8', c: '#6a4a30', s: '#8a2a2a', f: '#d8a07a', a: '#8a8a8a', l: '#2a2420' },
  { hat: 'fur', h: '#6a4a32', c: '#6a2a34', s: '#d8d0c0', f: '#e8c0a0', a: '#3a2418', l: '#1a1a22' },
  { hat: 'beanie', h: '#2a6a8a', c: '#3a5a3a', s: '#c8303a', f: '#a06a4a', a: '#1a1210', l: '#20242e' },
  { hat: 'bobble', h: '#2a8a4a', o: '#f4f0e8', H: '#c8303a', c: '#d8c8a8', s: '#2a4a8a', f: '#e8b890', a: '#c89048', l: '#2a2e3e' },
  { hat: 'beanie', h: '#e8c040', c: '#3a3a44', s: '#e8e0d0', f: '#c89070', a: '#2a1a12', l: '#18181e' },
  { hat: 'felt', h: '#1e2a22', o: '#c8a060', c: '#2e3a2e', s: '#6a2a2a', f: '#e0b090', a: '#6a5a4a', l: '#262a22' },
  { hat: 'fur', h: '#e8e4dc', c: '#b84a3a', s: '#2a2a3a', f: '#e8c0a0', a: '#8a5a2a', l: '#1e1e26' },
];
const darker = c => tint(c, '#05040a', 0.38);
function personRows(st, body, legs) { return [...HATS[st.hat], ...body, ...legs]; }
function personKey(st) { return { o: st.o || st.h, h: st.h, H: st.H || darker(st.h), a: st.a, f: st.f, s: st.s, c: st.c, C: darker(st.c), l: st.l, k: '#141018' }; }
// [style][frame] -> { r: facing right, l: facing left } (frames 0-1 walking), and a back view for the counters
let WALK_SPR, BACK_SPR;
function buildPeople() {
  WALK_SPR = STYLES.map(st => [0, 1].map(f => {
    const rows = personRows(st, BODY_SIDE, LEGS[f]), key = personKey(st);
    return { r: sprite(rows, key), l: sprite(rows, key, true) };
  }));
  BACK_SPR = STYLES.map(st => sprite(personRows(st, BODY_BACK, LEGS[2]), personKey(st)));
}

/* ================= The pretzel bakery's sign on its timber scaffold ================= */
// Two posts rising from behind the Glühwein stall's roof, just right of the Rathaus tower, with a snowy cross brace, a
// framed board under a little snow-covered gable roof, and two lamps hanging from its eaves. The city's own ad: a golden
// pretzel and BREZN between rows of blue-and-white lozenges; render(t, { pitch: true }) paints the pitch on the face
// instead (all else identical).
// It is a painted board in the market's own light, not a screen: the paint is night-graded (BB_NIGHT: dimmed, and pulled
// towards the timber of its frame, with the run-off of a winter under that little roof), its two lamps and its marquee
// bulbs are a stall's bulbs a step down, and their halo stays well under the Rathaus tower's. The tower beside it and
// the stalls' lit openings below it are the bright things here.
const BB_NIGHT = { level: 0.62, tint: WOOD[3], mix: 0.14 };
const BB_STEAM = tint(tint('#ffffff', '#000000', 1 - BB_NIGHT.level), BB_NIGHT.tint, BB_NIGHT.mix);   // the wisp drawn per frame over the pretzel, graded like the paint
const BB_BULB = { on: '#e8b860', off: '#4e3c26' };        // marquee bulbs (the stalls' are #ffd878 and #7a5428)
const BB_LENS = ['#ffd88a', '#a8824e', '#4a3a30'];        // a lamp's lens: lit, buzzing, out
const BB_POSTS = [BB.x + 8, BB.x + BB.w - 10];
const BB_LAMPS = [BB.x + 9, BB.x + BB.w - 10];            // lamp heads under the canopy's eaves
const BB_LAMP_Y = BB.y - 2;
const BBC = { x: BB.x - 6, y: BB.y - 12, w: BB.w + 12, h: 85 };   // structure sprite (the posts reach down behind the stall)
const BB_FLICK = [[71, 72.3], [183.5, 184.6]];            // the right lamp buzzes now and then
function renderBillboard() {
  const p = painter(BBC.w, BBC.h), put = (i, y, col) => p.put(i - BBC.x, y - BBC.y, col);
  const X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const px0 of BB_POSTS) for (let y = Y1 + 1; y < BBC.y + BBC.h; y++) { put(px0, y, WOOD[4]); put(px0 + 1, y, WOOD[1]); }
  // cross brace between the posts, snow along it
  const [a, b] = [BB_POSTS[0] + 2, BB_POSTS[1] - 1], ya = Y1 + 4, yb = Y1 + 26;
  for (let i = a; i <= b; i++) {
    const f = (i - a) / (b - a), y1 = Math.round(lerp(ya, yb, f)), y2 = Math.round(lerp(yb, ya, f));
    put(i, y1, WOOD[3]); put(i, y2, WOOD[2]);
    if (hash(i, 1, 61) < 0.5) put(i, y2 - 1, '#dfe6f4');
    put(i, Y1 + 2, WOOD[3]);
    if (hash(i, 2, 61) < 0.7) put(i, Y1 + 1, snowPx(hash(i, 3, 61), 0.4));
  }
  // frame around the face
  for (let i = X0; i <= X1; i++) { put(i, Y0, WOOD[1]); put(i, Y0 + 1, WOOD[3]); put(i, Y1, WOOD[0]); put(i, Y1 - 1, WOOD[2]); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, WOOD[1]); put(X0 + 1, y, WOOD[4]); put(X1, y, WOOD[0]); put(X1 - 1, y, WOOD[2]); }
  // little gable roof over the board, deep in snow, icicles off its eaves
  const cx = BB.x + BB.w / 2, hw = BB.w / 2 + 5;
  for (let i = Math.floor(cx - hw); i <= Math.ceil(cx + hw); i++) {
    const top = Math.round(Y0 - 1 - (1 - Math.abs(i - cx) / hw) * 6);
    for (let y = top; y < Y0; y++) {
      const k = y - top;
      put(i, y, k === 0 ? '#f2f6ff' : k === 1 ? snowPx(hash(i, 4, 61), hash(i, 5, 61)) : y === Y0 - 1 ? '#8a2420' : '#c83a30');
    }
    if (hash(i, 6, 61) < 0.08) { put(i, Y0, '#dfe8f6'); if (hash(i, 7, 61) < 0.5) put(i, Y0 + 1, '#8ea4c8'); }
  }
  // lamp housings under the eaves (their lenses are drawn per frame, just below)
  for (const lx of BB_LAMPS) for (let i = lx - 2; i <= lx + 2; i++) put(i, BB_LAMP_Y - 1, i === lx - 2 ? '#5a5660' : '#1e1c24');
  return p.done();
}
const PRETZEL = [
  '...bbb...bbb...',
  '..bBBBb.bBBBb..',
  '.bB...bbb...Bb.',
  'bB.....b.....Bb',
  'bB....bBb....Bb',
  'bB...bB.Bb...Bb',
  '.bB.bB...Bb.Bb.',
  '..bBb.....bBb..',
  '..bBBb...bBBb..',
  '...bBBbbbBBb...',
  '....bbBBBbb....',
  '......bbb......',
];
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    let col;
    if (y < 2 || y >= h - 2) col = (((i >> 1) + (y >> 1)) & 1) ? '#2c5cbc' : '#f6f0e0';     // blue-and-white lozenges (the white of old paint in lamplight)
    else {
      const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + i - lx))) / 12, v = (y - 2) / (h - 4) * 0.75 + lamp * 0.3;
      const PAPER = ['#ffefc4', '#fbe0a8', '#f2cc8c', '#e4b878'];                            // cream paint, gold where the lamps reach (graded below)
      const f = clamp(v, 0, 1) * 3; let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
      col = PAPER[Math.min(3, k)];
    }
    p.put(i, y, col);
  }
  stamp(p, PRETZEL, { b: '#7a3a16', B: '#d88c3e' }, 3, 4);
  for (const [sx, sy] of [[4, 5], [12, 5], [7, 12], [2, 8], [13, 9]]) p.put(sx + 1, sy + 1, '#ffffff');   // salt
  const c = p.done();
  const [tc, tx] = makeCanvas(textWidth('BREZN'), 5);                                          // tall letters
  text(tx, 'BREZN', 0, 0, '#1c3a8c');
  p.x.drawImage(tc, 0, 0, tc.width, 5, 22, 5, tc.width, 10);
  return gradeAd(c, { ...BB_NIGHT, wear: 0.4, seed: 5 });
}
// marquee bulbs chasing along the lozenge rows, a wisp of warmth rising off the pretzel
function drawBillboardAd(t) {
  ctx.drawImage(BB_AD, BB.x, BB.y);
  const s = step(t + 0.19, 720);
  for (let i = 1, k = 0; i < BB.w - 1; i += 3, k++) {
    const on = (k + s) % 4 !== 0;
    ctx.fillStyle = on ? BB_BULB.on : BB_BULB.off;
    ctx.fillRect(BB.x + i, BB.y, 1, 1); ctx.fillRect(BB.x + BB.w - 1 - i, BB.y + BB.h - 1, 1, 1);
  }
  const g = cycle(t, 80, 0.3);
  if (g.u < 0.6) {
    ctx.globalAlpha = 0.7 * Math.sin(Math.PI * g.u / 0.6);
    ctx.fillStyle = BB_STEAM;
    ctx.fillRect(BB.x + 9 + Math.round(Math.sin(g.u * 9)), BB.y + 4 - Math.round(g.u * 3), 1, 1);
    ctx.fillRect(BB.x + 11 + Math.round(Math.sin(g.u * 9 + 2)), BB.y + 3 - Math.round(g.u * 2), 1, 1);
    ctx.globalAlpha = 1;
  }
}
function bbLampI(t, i) {
  let v = 0.95 + 0.05 * wave(t, 1800, 0.25 + i * 0.5);
  if (i === 1 && inEvent(t, BB_FLICK)) { const hv = hash(17, Math.floor(t * 24)); v *= hv > 0.5 ? 1 : hv > 0.22 ? 0.4 : 0.1; }
  return v;
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.8);
  ctx.drawImage(BB_C, BBC.x, BBC.y);
  if (!PITCH.covers(t, pitch)) drawBillboardAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    const li = bbLampI(t, i);
    if (li < 0.9) { ctx.globalAlpha = (0.9 - li) * 0.5; rect(ctx, lx - 8, BB.y, 17, 8, '#1a0e06'); ctx.globalAlpha = 1; }
    ctx.fillStyle = BB_LENS[li > 0.6 ? 0 : li > 0.2 ? 1 : 2];
    ctx.fillRect(lx - 1, BB_LAMP_Y, 3, 1);
    halo(HL.bbLamp, lx, BB_LAMP_Y + 1, li);
  });
}

/* ================= Our stall: post, carved valance, gingerbread hearts, lantern, the counter and a mug ================= */
function renderForeground() {
  const p = painter(W, H), put = p.put;
  // corner post (lit on its left by the tree and the lantern)
  for (let y = 0; y < COUNTER; y++) for (let i = 0; i < 7; i++) {
    let col = i === 0 ? WOOD[5] : i === 1 ? WOOD[4] : i < 4 ? WOOD[3] : i < 6 ? WOOD[2] : WOOD[0];
    if (hash(POST + i, y >> 2, 62) < 0.08) col = WOOD[1];                // grain
    put(POST + i, y, col);
  }
  // valance: dark roof underside, a red carved board with a gold line and a scalloped edge
  for (let y = 0; y < 22; y++) for (let i = 398; i < W; i++) {
    const sc = (i - 398) % 8, sag = Math.round(Math.sqrt(Math.max(0, 16 - (sc - 3.5) * (sc - 3.5))));   // scallops
    if (y > 16 + sag) continue;
    let col;
    if (y < 3) col = y === 2 ? '#2a1612' : '#140a0a';
    else if (y === 13) col = '#e8b848';
    else if (y === 14) col = '#9a6a1e';
    else if (y > 14) col = y === 16 + sag ? '#4a1410' : '#8a2622';
    else { const v = (y - 3) / 10; col = v < 0.3 ? '#b8382e' : v < 0.8 ? '#a0302a' : '#7a2420'; if ((i - 398) % 16 === 8 && y > 4 && y < 12) col = '#e8b848'; }
    put(i, y, col);
  }
  // carved hearts on the board
  for (let i = 406; i < W; i += 16) for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 0], [0, 0], [1, 0], [0, 1]]) put(i + dx, 8 + dy, '#f4d070');
  // fir garland along the scalloped edge (bulbs per frame)
  for (let i = 396; i < W; i++) {
    const yb = 19 + Math.round(1.5 * Math.sin(i * 0.4));
    for (let k = -1; k <= 2; k++) if (hash(i, k, 63) < 0.8) put(i, yb + k, hash(i, k, 64) < 0.4 ? '#2c6a3c' : k > 0 ? '#0e2a18' : '#1a4a2a');
    if ((i - 396) % 4 === 1) FG_BULBS.push({ x: i, y: yb + 1, ph: hash(i, 1, 65) });
  }
  // counter: plank top, front edge, painted boards below; snow along the front edge (clear where the mug stands)
  for (let y = COUNTER; y < H; y++) for (let i = 350; i < W; i++) {
    let col;
    if (y <= COUNTER + 2) col = y === COUNTER ? '#d8a068' : '#a87244';
    else if (y <= COUNTER + 7) col = y === COUNTER + 7 ? '#2a160c' : i === 350 ? '#6a4228' : '#5a3620';
    else {
      const board = Math.floor((i - 350) / 5) & 1;
      col = (i - 350) % 5 === 4 ? '#3a1210' : board ? '#7a2420' : '#8e2a24';
      if (y === COUNTER + 8) col = '#1e0a08';
    }
    if (i === 350 && y > COUNTER) col = '#3a2014';
    put(i, y, col);
  }
  for (let i = 348; i < W; i++) {
    if (i >= MUG.x - 1 && i <= MUG.x + 13) continue;
    const hgt = 1 + Math.round(1.2 + Math.sin(i * 0.21) + hash(i >> 1, 1, 66) * 1.4);
    for (let k = 1; k <= hgt; k++) put(i, COUNTER - k + 1, k === hgt ? '#f4f8ff' : k === 1 ? '#aab6d6' : '#dfe6f4');
    if (hash(i, 2, 66) < 0.12) put(i, COUNTER + 3, '#cfdcf0');           // drips over the edge
  }
  // a chalkboard hanging on the post: PUNSCH and a price, a fir sprig on top
  const cbx = POST + 8, cby = 150;
  for (let y = cby; y < cby + 16; y++) for (let i = cbx; i < cbx + 27; i++) {
    const edge = y === cby || y === cby + 15 || i === cbx || i === cbx + 26;
    put(i, y, edge ? (y === cby ? WOOD[4] : WOOD[2]) : (bayer(i, y) < 0.2 ? '#2e3a36' : '#222c2a'));
  }
  for (let i = cbx + 3; i < cbx + 24; i += 2) put(i, cby - 1, hash(i, 1, 67) < 0.5 ? '#2c6a3c' : '#1a4a2a');
  put(cbx + 13, cby - 2, '#c8303a'); put(POST + 7, cby + 2, '#3a3440'); put(POST + 7, cby + 3, '#3a3440');
  // a paper cone of roasted almonds
  stamp(p, ['.aaaa.', 'aAaAaa', 'wwwwww', '.wwww.', '.wrrw.', '..ww..', '..ww..', '...w..'],
    { a: '#8a4a1e', A: '#c8803a', w: '#f0e6d0', r: '#c83a30' }, 432, COUNTER - 8);
  const c = p.done();
  text(p.x, 'PUNSCH', cbx + 2, cby + 3, '#e8ecf0');
  text(p.x, '4.50', cbx + 7, cby + 9, '#f4d070');
  return c;
}
const FG_BULBS = [];
// gingerbread hearts: brown, a scalloped white icing border, a squiggle of pink icing
function makeHeart(w) {
  const h = Math.round(w * 0.9), p = painter(w, h), r = w / 2;
  const inHeart = (i, y) => {
    const x = (i + 0.5 - r) / r, yy = 1.25 - (y + 0.5) / h * 2.3;
    return Math.pow(x * x + yy * yy - 1, 3) - x * x * yy * yy * yy < 0;
  };
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    if (!inHeart(i, y)) continue;
    const edge = !inHeart(i - 1, y) || !inHeart(i + 1, y) || !inHeart(i, y - 1) || !inHeart(i, y + 1);
    const inner = !edge && (!inHeart(i - 2, y) || !inHeart(i + 2, y) || !inHeart(i, y - 2) || !inHeart(i, y + 2));
    let col = edge ? '#4a220e' : inner ? ((i + y) & 1 ? '#fbf6ee' : '#e8e0d4') : i < r - 1 && y < h * 0.45 ? '#a8602e' : '#8a4a22';
    p.put(i, y, col);
  }
  const cy = Math.round(h * 0.42);
  for (let i = Math.round(r) - 2; i <= Math.round(r) + 1; i++) p.put(i, cy + ((i & 1) ? 1 : 0), '#ff7aa0');
  p.put(Math.round(r) - 1, cy + 2, '#7ad07a'); p.put(Math.round(r) + 1, cy + 2, '#7ad07a');
  return p.done();
}
const HEARTS = [{ x: 422, len: 11, w: 13, k: 36, ph: 0 }, { x: 441, len: 5, w: 9, k: 44, ph: 0.3 }, { x: 474, len: 16, w: 11, k: 40, ph: 0.6 }];
const MUG_ROWS = [
  '.rrrrrrrrr...',
  'rwdddddddwr..',
  'rRDDDDDDDRr..',
  '.RRRRRRRRR.hh',
  '.RRRRRRRRRR.h',
  '.RRRwRwRRRR.h',
  '.RRRwwwRRRR.h',
  '.RRRRwRRRRRh.',
  '.RRRRRRRRRhh.',
  '.RRRRRRRRR...',
  '.ssRRRRRRs...',
  '..sssssss....',
];
const MUG_KEY = { r: '#8ab0f0', w: '#f4ece0', d: '#5a0e1a', D: '#3a0810', R: '#2a4aa8', h: '#3a5ac0', s: '#162a6a' };
const LANTERN_ROWS = [
  '....k....',
  '...k.k...',
  '....k....',
  '...kKk...',
  '..kKKKk..',
  '.kKKKKKk.',
  'kkkkkkkkk',
  'k.......k',
  'k.......k',
  'k.......k',
  'k.......k',
  'k.......k',
  'k.......k',
  'kkkkkkkkk',
  '.kKKKKKk.',
  '...kkk...',
];

/* ================= The Glockenspiel show and the Frauenkirche bells ================= */
const SHOW = { a: 118, b: 164 };                      // the evening figures come out and turn
const CHIMES = [118.5, 122, 125.5, 129];              // the carillon's tune (four notes, 0.7 s apart, each)
const CHIME_WIN = CHIMES.map(c => [c, c + 2.9]);
const BELLS = [38, 41.5, 45];                         // the Frauenkirche strikes three
const WATCH_ROWS = ['.kk.', 'kkkk', '.ff.', 'cccL', 'ccc.', 'ccc.', '.cc.'];
const WATCH_KEY = { k: '#1c1a24', f: '#f0c090', c: '#3c4c62', L: '#ffe070' };
const ANGEL_ROWS = ['.gg.', '.ff.', 'ywwy', 'ywwy', '.ww.', '.ww.', 'www.'];
const ANGEL_KEY = { g: '#ffe070', f: '#f0c090', w: '#f6f2ea', y: '#e8b440' };
const KINDL_ROWS = ['.kk.', 'kffk', '.kk.', 'kkkg', 'kgk.', 'kkk.', 'kkk.'];
const KINDL_KEY = { k: '#161218', f: '#f0c090', g: '#f0c040' };
const COOPER_ROWS = ['.g.', 'g.g', '.f.', 'rwr', 'rrr', '.k.', 'k.k'];
const COOPER_KEY = { g: '#4ac060', f: '#f0c090', r: '#d8302e', w: '#f6f2ea', k: '#1c1a24' };
let FIGS, COOPERS;
function buildFigures() {
  const dim = key => Object.fromEntries(Object.entries(key).map(([k, v]) => [k, tint(v, '#2a160c', 0.55)]));
  const mk = (rows, key) => ({ r: sprite(rows, key), dl: sprite(rows, dim(key), true) });
  FIGS = [mk(WATCH_ROWS, WATCH_KEY), mk(ANGEL_ROWS, ANGEL_KEY), mk(KINDL_ROWS, KINDL_KEY)];
  const c = mk(COOPER_ROWS, COOPER_KEY);
  COOPERS = [c, c, c, c];
}
// figures on a turntable: the front half walks right, the back half walks left in shadow
function drawTurntable(list, floorY, spin, R, hop) {
  const n = list.length, order = [];
  for (let k = 0; k < n; k++) { const f = spin + k * TAU / n; order.push({ k, x: Math.sin(f) * R, z: Math.cos(f), f }); }
  order.sort((a, b) => a.z - b.z);
  for (const o of order) {
    const S = list[o.k], spr = o.z >= 0 ? S.r : S.dl;
    const hy = hop && Math.sin(o.f * 3) > 0.3 ? 1 : 0;
    ctx.drawImage(spr, Math.round(TX + o.x - spr.width / 2), floorY - spr.height + 1 - hy);
  }
}
function drawOriel(t) {
  if (inEvent(t, CHIME_WIN)) {                                             // the carillon's bells glint as they ring
    const s = step(t, 2400);
    for (let k = 0; k < 7; k++) if (hash(k, s, 91) < 0.45) px(ctx, ORIEL.x0 + 1 + 2 * k, ORIEL.up - 2, '#fff6c8');
    halo(HL.bells, TX, ORIEL.up - 2, 0.7);
  }
  const open = env(t, SHOW.a, SHOW.b, 3), g = Math.round(open * 6) - 1;
  if (g < 0) return;
  for (const y0 of [ORIEL.up, ORIEL.lo]) for (let ly = 0; ly <= 9; ly++) {  // shutters part on a lit room
    const w = Math.min(ly === 0 ? 2 : ly === 1 ? 4 : 5, g);
    ctx.fillStyle = ly < 4 ? '#ffdc98' : ly < 7 ? '#f6b466' : '#dc8c46';
    ctx.fillRect(TX - w, y0 + ly, 2 * w + 1, 1);
  }
  ctx.save();
  ctx.beginPath(); ctx.rect(TX - g, ORIEL.up, 2 * g + 1, ORIEL.lo + 10 - ORIEL.up); ctx.clip();
  const spin = TAU * (t - SHOW.a) / 9;
  drawTurntable(COOPERS, ORIEL.up + 9, -spin * 1.4, 4, true);              // the coopers dance above
  drawTurntable(FIGS, ORIEL.lo + 9, spin, 4, false);                       // watchman, angel and Kindl turn below
  ctx.restore();
  halo(HL.oriel, TX, ORIEL.lo + 14, open * 0.8);
}
function drawBelfries(t) {
  let a = 0.22;
  for (const b of BELLS) if (t >= b && t < b + 3.2) a = Math.max(a, Math.pow(1 - (t - b) / 3.2, 1.4));
  ctx.globalAlpha = Math.min(1, a * 1.1); ctx.fillStyle = '#ffcc7a';
  for (const cx of FK) for (const ox of [-2, 2]) ctx.fillRect(cx + ox, 65, 1, 7);
  ctx.globalAlpha = 1;
  for (const cx of FK) halo(HL.belfry, cx, 68, a);
}
function drawTowerLife(t, clock) {
  hands(ctx, CLOCK.x, CLOCK.y, clock, 540, 2, 3, '#2a1c14');
  halo(HL.towerLow, TX, 120, 0.95); halo(HL.towerHi, TX, 66, 0.9 + 0.1 * wave(t, 8, 0.25));
  halo(HL.clock, CLOCK.x, CLOCK.y, 0.9);
  const la = 0.55 + 0.15 * wave(t, 20, 0.25);                               // warm light in the lantern's arches
  ctx.globalAlpha = la; ctx.fillStyle = '#ffc878';
  for (const [a, b] of [[TX - 5, TX - 4], [TX - 1, TX + 1], [TX + 4, TX + 5]]) ctx.fillRect(a, 47, b - a + 1, 6);
  ctx.globalAlpha = 1;
  halo(HL.arches, TX, 49, la);
  halo(HL.loggia, RIS, LOGGIA_Y + 5, 0.85 + 0.1 * wave(t, 12, 0.6));       // warm light in the old part's loggia
  if (frac(t / 3 + 0.5) < 0.08) { px(ctx, TX, 10, '#ffffff'); halo(HL.glint, TX, 10, 0.8); }   // the Kindl catches a floodlight
  drawOriel(t);
}

/* ================= Windows, the paper star, the tree, the bulbs ================= */
function drawWindows(t) {
  for (const w of RWIN) if (hash(w.id + 50, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < 0.55) {
    ctx.fillStyle = '#ffd48c'; ctx.fillRect(w.x + 1, w.y, 1, 1); ctx.fillRect(w.x, w.y + 1, 3, 2);
    ctx.fillStyle = '#f2ac5e'; ctx.fillRect(w.x, w.y + 3, 3, 4);
    ctx.fillStyle = '#8a5634'; ctx.fillRect(w.x + 1, w.y + 2, 1, 5);
  }
  for (const w of HWIN) if (hash(w.id + 90, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < 0.5) {
    ctx.fillStyle = '#ffd690'; ctx.fillRect(w.x, w.y, w.w, 2);
    ctx.fillStyle = '#f0aa5c'; ctx.fillRect(w.x, w.y + 2, w.w, w.h - 2);
  }
  if (STAR_WIN) {                                                           // a paper Advent star glowing in a window
    const { x, y } = STAR_WIN, g = 0.8 + 0.2 * wave(t, 12, 0.25);
    halo(HL.star, x, y, g);
    ctx.fillStyle = '#ffe8b0';
    for (const [dx, dy] of [[0, -2], [-2, -2], [2, -2], [-2, 2], [2, 2], [0, 2], [-2, 0], [2, 0]]) ctx.fillRect(x + dx, y + dy, 1, 1);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x - 1, y - 1, 3, 3);
  }
}
const TREE_COL = ['#fff2c8', '#ffc860', '#ff7058'];
const TREE_STAR = ['...Y...', '..YYY..', 'YYYWYYY', '.YYWYY.', '..YYY..', '.YY.YY.', 'Y.....Y'];
function drawTree(t) {
  halo(HL.tree, TREE.x, 140, 0.9 + 0.1 * wave(t, 6, 0.25));
  const s = step(t + 0.17, 600);
  for (let i = 0; i < TREE_LIGHTS.length; i++) {
    const L = TREE_LIGHTS[i];
    if (hash(i, s, 93) < 0.1) { ctx.fillStyle = '#6a4a22'; ctx.fillRect(L.x, L.y, 1, 1); continue; }
    ctx.fillStyle = wave(t, 24, L.y / 44) > 0.92 ? '#ffffff' : TREE_COL[L.c];   // a shimmer runs up the tree every 10 s
    ctx.fillRect(L.x, L.y, 1, 1);
  }
  const sp = 0.8 + 0.2 * wave(t, 30, 0.25);
  halo(HL.treeStar, TREE.x, TREE.top - 2, sp);
  ctx.drawImage(TREE_STAR_C, TREE.x - 3, TREE.top - 6);
  if (frac(t / 4 + 0.3) < 0.1) {                                            // a glint of rays now and then
    ctx.fillStyle = '#fffbe0';
    ctx.fillRect(TREE.x, TREE.top - 10, 1, 3); ctx.fillRect(TREE.x - 5, TREE.top - 3, 2, 1); ctx.fillRect(TREE.x + 4, TREE.top - 3, 2, 1);
  }
}
// the light strings between the stalls: warm bulbs with a red one now and then, twinkling
function drawStringBulbs(t) {
  const s = step(t + 0.23, 480);
  for (let i = 0; i < STRING_BULBS.length; i++) {
    const b = STRING_BULBS[i], h = hash(i, s, 94);
    ctx.fillStyle = h < 0.1 ? '#6a4a28' : i % 5 === 2 ? '#ff6a50' : h < 0.2 ? '#ffffff' : '#ffe08a';
    ctx.fillRect(b.x, b.y, 1, 1);
  }
}
function drawBulbs(list, t, seed) {
  const s = step(t + (seed % 7) * 0.07, 480);
  for (let i = 0; i < list.length; i++) {
    const b = list[i], h = hash(i, s, seed);
    ctx.fillStyle = h < 0.08 ? '#7a5428' : h < 0.16 ? '#ffffff' : '#ffd878';
    ctx.fillRect(b.x, b.y, 1, 1);
  }
}

/* ================= The chestnut roaster's fire, the people ================= */
function drawRoasterFire(t) {
  const s = step(t + 0.09, 1440), { x, y } = ROAST;
  for (let i = 5; i < 19; i += 2) for (let j = 4; j < 9; j++) {
    const h = hash(i, j, s);
    ctx.fillStyle = h < 0.3 ? '#ffd060' : h < 0.72 ? '#ff7a2a' : '#a82a14';
    ctx.fillRect(x + i, y + j, 1, 1);
  }
  halo(HL.ember, x + 12, y + 6, 0.82 + 0.1 * wave(t, 900, 0.25) + 0.08 * wave(t, 1730, 0.25));
}
// the glints in the snow come and go slowly, each for a few seconds of its cycle
function drawSnowGlints(t) {
  ctx.fillStyle = '#fff6e0';
  for (const g of SNOW_GLINTS) {
    const v = wave(t, g.k, g.ph);
    if (v < 0.93) continue;
    ctx.globalAlpha = (v - 0.93) / 0.07;
    ctx.fillRect(g.x, g.y, 1, 1);
  }
  ctx.globalAlpha = 1;
}
const CUSTOMERS = [[3, 6], [5, 28], [0, 128], [7, 150], [2, 176], [4, 214], [6, 238], [1, 320], [5, 343], [0, 366], [2, 428], [6, 452]];
const CUST_FEET = 231;
function drawCustomers(t) {
  const s = step(t + 0.7, 160);
  CUSTOMERS.forEach(([st, x], i) => {
    const spr = BACK_SPR[st], bob = hash(i, s, 95) < 0.22 ? 1 : 0;
    ctx.drawImage(spr, x, CUST_FEET - spr.height + 1 + bob);
    if (hash(i, s >> 2, 96) < 0.3) px(ctx, x + (i & 1 ? 1 : 5), CUST_FEET - 8, '#b02838');   // a mug raised
  });
}
// people strolling in front of the stalls: [style, crossings per loop, phase, direction, feet row], nearest last
const WALKERS = [[3, 6, 0.15, -1, 238], [2, 6, 0.62, 1, 239], [0, 7, 0.33, -1, 240], [4, 5, 0.85, -1, 242], [0, 5, 0.05, 1, 244],
  [5, 4, 0.72, -1, 245], [6, 5, 0.5, 1, 247], [4, 6, 0.9, 1, 249], [1, 4, 0.4, -1, 250], [7, 4, 0.25, 1, 252]];
const DOG_ROWS = [['......e.', '.....ddd', 'tdddddd.', '.d...d..'], ['......e.', '.....ddd', 'tdddddd.', '..d.d...']];
const DOG_KEY = { e: '#3a1e0e', d: '#7a4020', t: '#7a4020' };
let DOG_SPR;
function drawWalker(i, t, fr) {
  const [st, n, ph, dir, fy] = WALKERS[i];
  const u = frac(t * n / LOOP + ph), x = Math.round(dir > 0 ? -14 + u * (W + 28) : W + 14 - u * (W + 28));
  if (x < -12 || x > W + 6) return;
  const f = (fr + i) & 1, S = WALK_SPR[st][f], spr = dir > 0 ? S.r : S.l;
  ctx.drawImage(spr, x - 3, fy - spr.height + 1 - f);
  if (st === 1) {                                                            // the man in the felt hat walks his dachshund
    const d = DOG_SPR[f][dir > 0 ? 0 : 1], dx = x + dir * 11;
    ctx.drawImage(d, dx - 4, fy - 3);
    line(ctx, x + dir * 2, fy - 8, dx + dir * 2, fy - 2, '#8a2a2a');
  }
}
// a father pulls his little girl on a wooden sledge along the front of the square at a stroll, three times a loop, in
// the runners' tracks; she sits facing forward, legs out, the sledge's runners curling up at its front
const SLED = { st: 2, n: 3, ph: 0.72, dir: 1, y: 255 };
const SLED_ROWS = ['..o.......', '.hhh......', '.aff......', '.sss......', 'cccc......', 'ccccLLk...', 'WWWWWWWWW.', '.d..d..d.W', 'RRRRRRRRR.'];
const SLED_KEY = { o: '#f4f0e8', h: '#c8303a', a: '#c89048', f: '#f0c8a0', s: '#e8c040', c: '#2a6aa8', L: '#20304e', k: '#141018',
  W: '#b8763e', d: '#643a22', R: '#3a2416' };
let SLED_SPR;
function drawSled(t, fr) {
  const { st, n, ph, dir, y } = SLED, u = frac(t * n / LOOP + ph);
  const x = Math.round(dir > 0 ? -14 + u * (W + 48) : W + 34 - u * (W + 48));
  const f = fr & 1, S = WALK_SPR[st][f], spr = dir > 0 ? S.r : S.l, front = x - dir * 10;
  ctx.drawImage(spr, x - 3, y - spr.height + 1 - f);
  ctx.drawImage(SLED_SPR[dir > 0 ? 0 : 1], dir > 0 ? front - 9 : front, y - SLED_ROWS.length + 1);
  line(ctx, x - dir * 2, y - 7 - f, front, y - 2, '#a08060');                 // the pull rope
}

// standing tables made from barrels, a candle jar and mugs on top, two people chatting at each
const TABLES = [{ x: 106, y: 249, a: 3, b: 6 }, { x: 292, y: 251, a: 7, b: 0 }];
const TABLE_ROWS = ['..sssssssss..', '.ttttttttttt.', '.TTTTTTTTTTT.', '....bbbbb....', '...bBbbbbb...', '...hhhhhhh...',
  '...bBbbbbb...', '...bBbbbbb...', '...bBbbbbb...', '...hhhhhhh...', '...bBbbbbb...', '....bbbbb....'];
const TABLE_KEY = { s: '#eef2fc', t: '#c08250', T: '#5a3620', b: '#7a4a26', B: '#a86a3a', h: '#3a3440' };
let TABLE_C;
function drawTable(tb, t, k) {
  const { x, y } = tb, top = y - 11;
  const s = step(t + 0.9 + 0.31 * k, 120);
  for (const [st, side] of [[tb.a, -1], [tb.b, 1]]) {                        // the two of them face each other
    const spr = side < 0 ? WALK_SPR[st][1].r : WALK_SPR[st][1].l, px0 = side < 0 ? x - 13 : x + 6;
    const lean = hash(k * 2 + side, s, 97) < 0.3 ? side : 0;                  // leaning in to talk now and then
    ctx.drawImage(spr, px0 - lean, y - spr.height + 1);
    if (hash(k * 2 + side, s, 98) < 0.25) px(ctx, side < 0 ? px0 + 5 : px0 + 1, y - 12, '#c8303a');   // a sip
  }
  ctx.drawImage(TABLE_C, x - 6, top);
  rect(ctx, x - 4, top - 2, 1, 2, '#b02838'); rect(ctx, x + 3, top - 2, 1, 2, '#2a4a9a');       // mugs
  px(ctx, x, top - 1, hash(k, step(t + 0.2, 1920), 99) < 0.2 ? '#ffe8a0' : '#ffc860');          // candle jar
  halo(HL.candle, x, top - 1, 0.85 + 0.15 * wave(t, 400 + 60 * k, 0.25));
  puffs(MUG_STEAM, t, x - 4, top - 3, 12, -4, 2, 0.28, PUFF_W);
}
const CROWD = [...WALKERS.map((w, i) => ({ y: w[4], walker: i })), ...TABLES.map((tb, i) => ({ y: tb.y, table: i })),
  { y: SLED.y, sled: true }].sort((a, b) => a.y - b.y);
function drawCrowd(t) {
  const fr = step(t + 0.13, 960);
  for (const c of CROWD) {
    if (c.sled) drawSled(t, fr);
    else if (c.walker === undefined) drawTable(TABLES[c.table], t, c.table);
    else drawWalker(c.walker, t, fr);
  }
}

/* ================= Steam and smoke ================= */
const STEAM = [], SMOKE = [], MUG_STEAM = [];
{ const r = rng(61); for (let i = 0; i < 22; i++) STEAM.push({ n: 44 + Math.floor(r() * 12), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), s: 0.6 + r() * 0.8 }); }
{ const r = rng(62); for (let i = 0; i < 16; i++) SMOKE.push({ n: 30 + Math.floor(r() * 8), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), s: 0.7 + r() * 0.6 }); }
{ const r = rng(63); for (let i = 0; i < 9; i++) MUG_STEAM.push({ n: 60 + Math.floor(r() * 12), ph: r(), k: 30 + Math.floor(r() * 20), p: r(), s: 0.6 + r() * 0.8 }); }
function puffs(list, t, x0, y0, rise, drift, rMax, alpha, pal) {
  for (const s of list) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = x0 + (0.35 * u + 0.65 * u * u) * drift * s.s + wave(t, s.k, s.p) * 2 * u;
    const y = y0 - u * rise + u * u * rise * 0.25;
    const rr = Math.max(1, Math.min(rMax, 1 + Math.floor(u * (rMax + 1))));
    ctx.globalAlpha = alpha * Math.min(1, u * 14) * Math.pow(1 - u, 1.2);
    ctx.drawImage(pal[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}

/* ================= Our stall, per frame ================= */
const LANTERN_FLICK = [[88, 89.4], [203, 204.2]];
function lanternI(t) {
  let v = 0.9 + 0.05 * wave(t, 1320, 0.25) + 0.03 * wave(t, 2270, 0.25);
  if (inEvent(t, LANTERN_FLICK)) v *= 0.7 + 0.3 * hash(22, Math.floor(t * 18));
  return v;
}
function drawForeground(t) {
  ctx.drawImage(FG_C, 0, 0);
  drawBulbs(FG_BULBS, t, 98);
  for (const hh of HEARTS) {                                                // gingerbread hearts on their ribbons
    const sw = wave(t, hh.k, hh.ph) * 2.2 + wave(t, hh.k * 3 + 1, hh.ph) * 0.4;
    const by = 21 + hh.len, bx = Math.round(hh.x + sw);
    line(ctx, hh.x, 21, bx, by, '#c8303a');
    ctx.drawImage(hh.c, bx - (hh.c.width >> 1), by);
  }
  const fl = lanternI(t), { x, y } = LANTERN;                               // lantern: chain, glowing panes, candle
  for (let yy = 22; yy < y; yy++) px(ctx, x, yy, (yy & 1) ? '#3a3440' : '#1a1820');
  rect(ctx, x - 3, y + 7, 7, 6, fl > 0.85 ? '#ffd890' : '#e8b060');
  rect(ctx, x - 3, y + 7, 7, 2, '#ffe8b0');
  rect(ctx, x, y + 10, 1, 3, '#fff6e0');
  px(ctx, x, y + 9 - (hash(23, step(t + 0.05, 2400)) < 0.3 ? 1 : 0), '#ffffff');
  ctx.drawImage(LANTERN_C, x - 4, y);
  halo(HL.lantern, x, y + 10, fl); halo(HL.lanternCore, x, y + 10, fl);
  ctx.drawImage(MUG_C, MUG.x, MUG.y);                                       // the mug, steaming
  puffs(MUG_STEAM, t, MUG.x + 5, MUG.y - 1, 22, -6, 4, 0.5, PUFF_W);
}

/* ================= Snow at three depths ================= */
// warm light volumes the flakes glow in as they pass: { x, y, rx, ry, col }
const SNOW_LIGHTS = [
  { x: TREE.x, y: 132, rx: 36, ry: 58, col: '#ffe6b0' }, { x: MS.x, y: 101, rx: 10, ry: 10, col: '#ffeaa8' },
  { x: TX, y: 112, rx: 16, ry: 30, col: '#ffe2b4' }, { x: 24, y: 208, rx: 30, ry: 14, col: '#ffdca8' },
  { x: 159, y: 204, rx: 44, ry: 15, col: '#ffdca8' }, { x: 233, y: 208, rx: 28, ry: 13, col: '#ffdca8' },
  { x: 349, y: 206, rx: 40, ry: 13, col: '#ffdca8' }, { x: ROAST.x + 12, y: 206, rx: 16, ry: 10, col: '#ffb888' },
  { x: LANTERN.x, y: 44, rx: 28, ry: 24, col: '#ffe2a8' },
];
const SNOW_LIGHTS_BB = [...SNOW_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 1, rx: 22, ry: 6, col: '#f0dcb8' }];   // + the sign's lamps (small and dim)
const SNOW = [
  { count: 230, nMin: 18, nMax: 26, size: 1, col: 'rgba(168,184,222,0.6)', amp: 2.5, drift: -8, y1: [92, 128], lit: false },
  { count: 150, nMin: 28, nMax: 36, size: 1, col: 'rgba(224,232,250,0.92)', amp: 4, drift: -14, y1: [206, 252], lit: true },
  { count: 60, nMin: 40, nMax: 50, size: 2, col: 'rgba(242,246,255,0.95)', amp: 7, drift: -24, y1: [H + 4, H + 4], lit: true },
];
SNOW.forEach((L, li) => {
  const r = rng(300 + li);
  L.flakes = [];
  for (let i = 0; i < L.count; i++) L.flakes.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 20 + Math.floor(r() * 50), p: r() });
});
function drawSnow(L, t, lights) {
  const span = W + 40;
  for (const f of L.flakes) {
    const q = t * f.n / LOOP + f.ph, fq = Math.floor(q), cyc = ((fq % f.n) + f.n) % f.n, u = q - fq;
    const yEnd = lerp(L.y1[0], L.y1[1], hash(f.id, cyc, 2)), y = -4 + u * (yEnd + 4);
    let x = hash(f.id, cyc, 1) * span + u * L.drift + L.amp * Math.sin(TAU * (f.k * t / LOOP + f.p));
    x = ((x % span) + span) % span - 20;
    let col = L.col;
    if (L.lit) for (const l of lights) {
      const dx = (x - l.x) / l.rx, dy = (y - l.y) / l.ry;
      if (dx * dx + dy * dy < 1) { col = l.col; break; }
    }
    ctx.globalAlpha = L.y1[0] < H && u > 0.9 ? (1 - u) * 10 : 1;            // flakes settle into the snow
    ctx.fillStyle = col;
    ctx.fillRect(Math.round(x), Math.round(y), L.size, L.size);
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): Munich draws its own snow; the snow lying on the roofs, the stalls and the
// square stays in every pick. Rain and leaves fall behind the Rathaus, onto the square and past the bottom of the frame,
// and catch the same lights as the snow
const WEATHER = weatherPlans(['snow']);
const WX_LAND = [[96, 128], [206, 252], [H + 4, H + 4]];
// we stand under our stall's roof: right of its corner post nothing falls close by (the near depth is drawn over the
// stall), only out on the square behind it
const WX_SHELTER = [[], [], [[POST, 0, W, H]]];
const WX = { land: WX_LAND, lights: SNOW_LIGHTS, shelter: WX_SHELTER };
const WX_BB = { land: WX_LAND, lights: SNOW_LIGHTS_BB, shelter: WX_SHELTER };

/* ================= Vignette ================= */
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.75, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 4; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, CLOUDS, FAR_C, FK_C, RATH_C, MID_C, BACK_C, FRONT_C, FG_C, VIG_C, BB_C, BB_AD, PITCH,
  MUG_C, LANTERN_C, TREE_STAR_C, PUFF_W, PUFF_S;
function prerender() {
  HL = {
    glow: makeHalo(150, '#ff9a4a', 0.08, 1.3), fk: makeHalo(24, '#ffae6a', 0.14, 1.5), fkDome: makeHalo(12, '#7affc0', 0.1, 1.5),
    belfry: makeHalo(7, '#ffc070', 0.5, 1.3), towerLow: makeHalo(46, '#ffb466', 0.16, 1.6), towerHi: makeHalo(30, '#ffd6a0', 0.13, 1.6),
    clock: makeHalo(8, '#fff0c0', 0.35, 1.4), arches: makeHalo(10, '#ffc070', 0.3, 1.4), glint: makeHalo(4, '#ffffff', 0.6, 1),
    bells: makeHalo(10, '#ffe08a', 0.4, 1.4), oriel: makeHalo(16, '#ffb050', 0.45, 1.4), star: makeHalo(9, '#fff2d8', 0.4, 1.3),
    statue: makeHalo(16, '#ffd060', 0.32, 1.5), tree: makeHalo(56, '#ffc070', 0.15, 1.5), treeStar: makeHalo(12, '#fff0b0', 0.6, 1.4),
    stallB: makeHalo(18, '#ffb060', 0.16, 1.4), stallF: makeHalo(34, '#ffb060', 0.2, 1.5), ember: makeHalo(14, '#ff7a2a', 0.4, 1.4),
    candle: makeHalo(7, '#ffc070', 0.4, 1.3), lantern: makeHalo(36, '#ffb060', 0.24, 1.6), lanternCore: makeHalo(9, '#ffe0a0', 0.6, 1.3),
    bbLamp: makeHalo(5, '#ffc070', 0.28, 1.5), bbWash: makeHalo(22, '#ffb060', 0.06, 1.5),   // the sign's two lamps: a stall bulb's warmth, well under the tower's glow
    loggia: makeHalo(18, '#ffb050', 0.16, 1.5),
  };
  SKY_C = renderSky();
  CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR_C = renderFar(); FK_C = renderFK();
  RATH_C = renderRathaus();
  { const p = painter(W, H); renderGround(p); const c = p.done(); RATH_C.getContext('2d').drawImage(c, 0, 0); }
  { const p = painter(W, H); renderTree(p); renderColumn(p); MID_C = p.done(); }
  BACK_C = renderBack(); FRONT_C = renderFront(); FG_C = renderForeground();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#5b8cff' });
  for (const hh of HEARTS) hh.c = makeHeart(hh.w);
  MUG_C = sprite(MUG_ROWS, MUG_KEY);
  LANTERN_C = sprite(LANTERN_ROWS, { k: '#16141c', K: '#3a3644' });
  TREE_STAR_C = sprite(TREE_STAR, { Y: '#ffd050', W: '#fffbe0' });
  TABLE_C = sprite(TABLE_ROWS, TABLE_KEY);
  DOG_SPR = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  SLED_SPR = [sprite(SLED_ROWS, SLED_KEY), sprite(SLED_ROWS, SLED_KEY, true)];
  buildPeople(); buildFigures();
  PUFF_W = [null]; PUFF_S = [null];
  for (let r = 1; r <= 9; r++) { PUFF_W.push(makePuff(r, '#f6eee6', '#cac2c2')); PUFF_S.push(makePuff(r, '#d0c6c2', '#948c96')); }
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
// pitch: the "your ad here" face (promo captures); billboard: false leaves the sign out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch = false, billboard = true, weather, clock, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), lights = billboard ? SNOW_LIGHTS_BB : SNOW_LIGHTS, wxc = billboard ? WX_BB : WX;
  // sky: stars through the gaps, drifting snow clouds, the market's glow
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#dfe8ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  halo(HL.glow, 270, 206, 1);
  // old-town roofs, the Frauenkirche
  ctx.drawImage(FAR_C, 0, 0);
  for (const cx of FK) { halo(HL.fk, cx, 96, 0.9); halo(HL.fkDome, cx, 52, 0.85); }
  ctx.drawImage(FK_C, 0, 0);
  drawBelfries(t);
  if (wx.snow) drawSnow(SNOW[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the Neues Rathaus, the houses round the square
  ctx.drawImage(RATH_C, 0, 0);
  drawWindows(t);
  drawTowerLife(t, clock);
  // the Mariensäule and the Christmas tree
  ctx.drawImage(MID_C, 0, 0);
  halo(HL.statue, MS.x, MS.top + 5, 0.9 + 0.1 * wave(t, 10, 0.25));
  drawTree(t);
  // back row of stalls
  ctx.drawImage(BACK_C, 0, 0);
  drawBulbs(BULBS_BACK, t, 96);
  for (const s of BACK_STALLS) halo(HL.stallB, (s.x0 + s.x1) >> 1, s.counter - 3, 0.85);
  if (billboard) drawBillboard(t, pitch);
  // front row, the roaster, the people
  ctx.drawImage(FRONT_C, 0, 0);
  drawBulbs(BULBS_FRONT, t, 97);
  drawStringBulbs(t);
  for (const s of FRONT_STALLS) halo(HL.stallF, (s.x0 + s.x1) >> 1, s.counter - 2, 0.9);
  drawRoasterFire(t);
  drawSnowGlints(t);
  drawCustomers(t);
  puffs(STEAM, t, 213, 208, 46, -20, 7, 0.5, PUFF_W);
  puffs(SMOKE, t, ROAST.x + 24.5, ROAST.y - 19, 60, -26, 7, 0.55, PUFF_S);
  drawCrowd(t);
  if (wx.snow) drawSnow(SNOW[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // our stall, then the nearest snow
  drawForeground(t);
  if (wx.snow) drawSnow(SNOW[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'munich',
  name: 'Munich',
  tagline: 'Christmas market in the snow',
  timeZone: 'Europe/Berlin',
  accent: '#5b8cff',
  focusX: TX,
  landmark: [FK[0] - 7, TX + 14],                      // the Frauenkirche's domes and the Neues Rathaus tower
  landmarkTop: 10,                                     // the row of the Rathaus tower's finial, which landscape crops keep
  billboard: BB,
  ambience: { crowd: 0.4, wind: 0.2 },
  events: [
    ...BELLS.map(t => ({ t, type: 'bell', x: (FK[0] + FK[1]) >> 1, gain: 0.5 })),
    ...CHIMES.map(t => ({ t, type: 'chime', x: TX, gain: 0.55 })),
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
